// ─── PART 7: FILINGS + ALT DATA + DEEP SIGNALS ───────────────────────────────
// Dependencies: part1_core (C, s, fmt, fmtB, fmtK, DataStatus, AnalystButton)
// Exports: FilingsTab, AltDataTab, DeepSignalsTab
//
// FilingsTab:
//   SEC EDGAR full-text search API (no key) — 10-K, 10-Q, 8-K, 13D, 13G, Form 4
//   AI filing analyzer — paste any section for structured analysis
//
// AltDataTab (5 panels):
//   MARITIME   — live AIS vessel positions (AISStream.io, beat-warmed)
//   IMPORTS    — U.S. Customs Bills of Lading via ImportYeti methodology
//   PETROLEUM  — EIA weekly inventory (api.eia.gov, key optional)
//   CONGRESS   — STOCK Act disclosures (Quiver Quantitative via proxy)
//   THERMAL    — NASA FIRMS satellite fire/thermal anomaly data
//
// DeepSignalsTab (5 panels):
//   CONTRACTS  — USASpending.gov federal awards (free, no key, live)
//   FDA        — openFDA drug approvals and pipeline (free, no key)
//   PATENTS    — USPTO Open Data Portal (api.uspto.gov, KEY REQUIRED, server-side)
//   COURTS     — CourtListener / RECAP PACER (free, no key via proxy)
//   BANKS      — FDIC BankFind Call Report data (free, no key)
//
// onData callbacks:
//   AltDataTab  → "tankers", "fires", "congressTrades", "petroleum"
//   DeepSignals → "contracts"

import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { AlertCircle, RefreshCw } from "lucide-react";
import { SearchBox, useRemoteSearch } from "./components/SearchBox";
import {
  C, s, font, fmt, fmtB, fmtK, fmtUsd,
  DataStatus, AnalystButton,
  EIA_BASE, backendFetch,
} from "./core";
import { liveRows } from "./placeholder";

// ═══════════════════════════════════════════════════════════════════════════════
// FILINGS TAB
// ═══════════════════════════════════════════════════════════════════════════════

// ⚠ backendFetch returns a Response; every call here goes through the platform so
// the SEC User-Agent and rate limit apply server-side. The previous version fetched
// efts.sec.gov straight from the browser — which worked (EDGAR sends
// access-control-allow-origin: *) but bypassed both.
async function filingsGet(path) {
  const res = await backendFetch(path, path);
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}

// Form badge colours. ⚠ Keys match what /api/edgar/search normalises `form_type`
// to — EDGAR's root_forms values ("10-K", "8-K", "SC 13D"), NOT the `file_type`
// field, which is the EXHIBIT type ("EX-21.1") and was what the old mapping read.
const FORM_COLORS = {
  "10-K": C.amber, "10-Q": C.teal, "8-K": C.orange,
  "SC 13D": C.red, "SC 13G": C.purple, "4": C.blue,
};

// Filing-index group colours, keyed on the group labels the endpoint returns.
const FILING_GROUP_COLORS = { "8-K": C.orange, "13D/13G": C.red, "DEF 14A": C.purple };

export function FilingsTab({ keys }) {
  // ── Sections 1 + 2: ticker-driven ──
  const [ticker,        setTicker]        = useState("");
  const [submitted,     setSubmitted]     = useState("");
  const [insider,       setInsider]       = useState(null);
  const [filings,       setFilings]       = useState(null);
  const [tickerLoading, setTickerLoading] = useState(false);
  const [tickerErr,     setTickerErr]     = useState("");
  const [openGroups,    setOpenGroups]    = useState({});
  const [aircraft,      setAircraft]      = useState(null);

  // ── Section 3: EDGAR full-text (independent of the ticker) ──
  const [query,     setQuery]     = useState("");
  const [results,   setResults]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [searchErr, setSearchErr] = useState("");

  // ── Section 4: analyzer (unchanged) ──
  const [pasted,    setPasted]    = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis,  setAnalysis]  = useState("");

  const loadTicker = async () => {
    const sym = ticker.trim().toUpperCase();
    if (!sym) return;
    setTickerLoading(true);
    setTickerErr("");
    setInsider(null);
    setFilings(null);
    setAircraft(null);
    setSubmitted(sym);
    // ⚠ allSettled, not all: the insider feed and the filing index are independent
    // sources and one being cold must not blank the other. A ticker with no Form 4
    // history can still have 8-Ks indexed, and vice versa.
    const [insRes, filRes, airRes] = await Promise.allSettled([
      filingsGet(`/api/insider/trades/${encodeURIComponent(sym)}?days=90`),
      filingsGet(`/api/filings/${encodeURIComponent(sym)}/index`),
      filingsGet(`/api/filings/${encodeURIComponent(sym)}/aircraft`),
    ]);
    if (insRes.status === "fulfilled") setInsider(insRes.value);
    if (airRes.status === "fulfilled") setAircraft(airRes.value);
    if (filRes.status === "fulfilled") {
      setFilings(filRes.value);
      // ⚠ Open the first NON-EMPTY group. Opening the first group unconditionally
      // would show "no 8-K filings indexed" to a ticker whose only rows are 13Gs,
      // which reads as nothing found.
      const first = (filRes.value?.groups || []).find((g) => g.count > 0);
      setOpenGroups(first ? { [first.group]: true } : {});
    }
    if (insRes.status === "rejected" && filRes.status === "rejected"
        && airRes.status === "rejected") {
      setTickerErr(`Could not load filings for ${sym}. Try again in a moment.`);
    }
    setTickerLoading(false);
  };

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResults([]);
    setSearchErr("");
    try {
      // ⚠ Server-side now. The endpoint adds the `enddt` EDGAR silently requires —
      // without it the date filter is ignored entirely and results reach back to
      // 2001 under a panel that says "2024+".
      const data = await filingsGet(`/api/edgar/search?q=${encodeURIComponent(query.trim())}`);
      const rows = Array.isArray(data?.results) ? data.results : [];
      if (rows.length) setResults(rows);
      else setSearchErr(`No filings found for "${query}" since 2024. Try company name, ticker, or person.`);
    } catch {
      setSearchErr("Data temporarily unavailable. Try again in a moment.");
    }
    setLoading(false);
  };

  const analyze = async () => {
    if (!pasted.trim()) return;
    setAnalyzing(true);
    try {
      // Platform AI endpoint — server key, every authed user, shared
      // filing_analyzer prompt + NOUS_VOICE. Unchanged.
      const res = await backendFetch(
        "https://api.anthropic.com/v1/messages",   // direct fallback (local dev)
        "/api/ai/analyze",
        {
          method: "POST",
          body: JSON.stringify({
            panel:   "filing_analyzer",
            context: `SEC filing excerpt:\n\n${pasted.slice(0, 4000)}`,
          }),
        }
      );
      if (!res.ok) {
        let msg = "Analysis unavailable.";
        try {
          const e = await res.json();
          const d = e && e.detail;
          if (res.status === 401) msg = "Please sign in to use the AI analyst.";
          else if (res.status === 403) msg = "AI analyst is not available on your plan.";
          else if (res.status === 429) msg = (typeof d === "string" && d) || "Daily AI limit reached. Try again tomorrow.";
          else if (d && typeof d === "object" && d.message) msg = d.message;
          else if (typeof d === "string" && d) msg = d;
        } catch {}
        setAnalysis(msg);
        setAnalyzing(false);
        return;
      }
      const data = await res.json();
      setAnalysis(data.analysis ?? "Analysis unavailable.");
    } catch {
      setAnalysis("Connection failed. Check your network.");
    }
    setAnalyzing(false);
  };

  const trades = (insider && Array.isArray(insider.trades)) ? insider.trades : [];
  const groups = (filings && Array.isArray(filings.groups)) ? filings.groups : [];
  const disclosures = (aircraft && Array.isArray(aircraft.disclosures))
    ? aircraft.disclosures : [];

  return (
    <div>
      {/* ── Ticker input — drives Sections 1 and 2 ────────────────────────── */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>COMPANY LOOKUP</span>
          <span style={{ ...s.tag(C.teal), fontSize: 11 }}>INSIDER + FILING INDEX</span>
        </div>
        <div style={s.panelB}>
          <div style={{ fontSize: 13, color: C.textDim, marginBottom: 8 }}>
            Enter a ticker to load Form 4 insider activity and the recent
            filing index held on the platform.
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              style={{ ...s.inp, flex: 1 }}
              placeholder="e.g. AAPL"
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadTicker()}
            />
            <button style={s.btn("amber")} onClick={loadTicker} disabled={tickerLoading}>
              {tickerLoading ? "LOADING..." : "LOAD"}
            </button>
          </div>
          {tickerErr && (
            <div style={{ color: C.red, fontSize: 13, marginTop: 8 }}>{tickerErr}</div>
          )}
        </div>
      </div>

      {/* ── SECTION 1 — platform insider data ─────────────────────────────── */}
      {submitted && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>INSIDER ACTIVITY · {submitted}</span>
            <DataStatus status="live" source="Form 4" />
          </div>
          <div style={s.panelB}>
            {tickerLoading ? (
              <div style={{ color: C.textDim, fontSize: 13 }}>LOADING INSIDER ACTIVITY...</div>
            ) : trades.length === 0 ? (
              <div style={{ color: C.textDim, fontSize: 13 }}>
                No insider activity on record for {submitted}.
              </div>
            ) : (
              trades.slice(0, 10).map((t, i) => {
                // ⚠ Every field is optional-chained and omitted when absent. The
                // parsed Form 4 rows vary: owner_title is "" on directors, and a
                // derivative-only filing carries no share count.
                const shares = Number.isFinite(Number(t?.shares)) && Number(t.shares) > 0
                  ? `${Number(t.shares).toLocaleString()} sh` : null;
                const price = Number.isFinite(Number(t?.price)) && Number(t.price) > 0
                  ? `@ $${Number(t.price).toFixed(2)}` : null;
                const role = t?.is_director ? "Director" : (t?.is_officer ? "Officer" : (t?.is_ten_pct ? "10% owner" : ""));
                const sub = [t?.owner_title, role].filter(Boolean).join(" · ");
                const line = [shares, price].filter(Boolean).join(" ");
                return (
                  <div key={`${t?.owner_name || "x"}-${t?.transaction_date || i}`} style={{
                    borderTop: i === 0 ? "none" : `1px solid ${C.border}`,
                    padding: "8px 0",
                  }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                        {t?.owner_name || "—"}
                      </span>
                      {t?.transaction_type && (
                        <span style={{ ...s.tag(String(t.transaction_type).includes("BUY") ? C.green : C.textDim), fontSize: 11 }}>
                          {t.transaction_type}
                        </span>
                      )}
                      {t?.transaction_date && (
                        <span style={{ fontSize: 11, color: C.textDim }}>{t.transaction_date}</span>
                      )}
                    </div>
                    {sub && <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{sub}</div>}
                    {line && <div style={{ fontSize: 11, color: C.textDim, marginTop: 1 }}>{line}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ── SECTION 2 — filing index, collapsible per form family ─────────── */}
      {submitted && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>FILING INDEX · {submitted}</span>
            <DataStatus status="live" source="" />
          </div>
          <div style={s.panelB}>
            {tickerLoading ? (
              <div style={{ color: C.textDim, fontSize: 13 }}>LOADING FILING INDEX...</div>
            ) : filings && filings._source === "unknown_ticker" ? (
              <div style={{ color: C.textDim, fontSize: 13 }}>
                No company matches "{submitted}".
              </div>
            ) : groups.length === 0 ? (
              <div style={{ color: C.textDim, fontSize: 13 }}>
                No filing index available for {submitted}.
              </div>
            ) : (
              groups.map((g) => {
                const open = !!openGroups[g.group];
                const col = FILING_GROUP_COLORS[g.group] || C.amber;
                return (
                  <div key={g.group} style={{ borderTop: `1px solid ${C.border}` }}>
                    <div
                      onClick={() => setOpenGroups((p) => ({ ...p, [g.group]: !p[g.group] }))}
                      style={{
                        cursor: "pointer", padding: "8px 0", display: "flex",
                        alignItems: "center", gap: 6, minHeight: 44,
                      }}
                    >
                      <span style={{ fontSize: 11, color: C.textDim, width: 10 }}>
                        {open ? "▾" : "▸"}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: col }}>
                        {g.group} FILINGS
                      </span>
                      <span style={{ fontSize: 11, color: C.textDim }}>({g.count})</span>
                    </div>
                    {open && (g.count === 0 ? (
                      <div style={{ fontSize: 12, color: C.textDim, padding: "0 0 8px 16px" }}>
                        No {g.group} filings indexed.
                      </div>
                    ) : (
                      g.filings.map((f) => (
                        <div key={f.accession_number} style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "6px 0 6px 16px", flexWrap: "wrap",
                        }}>
                          <span style={{ fontSize: 12, color: C.textDim, minWidth: 82 }}>
                            {f.filed_date || "—"}
                          </span>
                          <span style={{ ...s.tag(col), fontSize: 10 }}>{f.form_type}</span>
                          <span style={{ fontSize: 13, color: C.text, flex: 1, minWidth: 120 }}>
                            {f.company_name || "—"}
                          </span>
                          {f.sec_url && (
                            <a
                              href={f.sec_url}
                              target="_blank"
                              rel="noreferrer"
                              style={{ fontSize: 12, color: C.blue, textDecoration: "none", flexShrink: 0 }}
                            >
                              VIEW &rarr;
                            </a>
                          )}
                        </div>
                      ))
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ── SECTION 3 — aircraft disclosures ──────────────────────────────── */}
      {submitted && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>AIRCRAFT DISCLOSURES · {submitted}</span>
            <DataStatus status="live" source="DEF 14A" />
          </div>
          <div style={s.panelB}>
            {tickerLoading ? (
              <div style={{ color: C.textDim, fontSize: 13 }}>LOADING DISCLOSURES...</div>
            ) : disclosures.length === 0 ? (
              /* ⚠ AN EMPTY RESULT IS THE NORMAL ANSWER, NOT A FAULT. Of 7 proxies
                 surveyed before the detector was built, ONE carried a genuine
                 aircraft perquisite. The wording says where the data comes from so
                 an empty panel reads as "nothing disclosed" rather than "broken". */
              <div style={{ color: C.textDim, fontSize: 13, lineHeight: 1.6 }}>
                No aircraft usage disclosures found in filings for {submitted}.
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  Sourced from DEF 14A proxy statements.
                </div>
              </div>
            ) : (
              disclosures.map((d, i) => (
                <div key={d.accession_number || i} style={{
                  borderTop: i === 0 ? "none" : `1px solid ${C.border}`,
                  padding: "10px 0",
                }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                      {d.company_name || submitted}
                    </span>
                    {d.form_type && (
                      <span style={{ ...s.tag(C.purple), fontSize: 10 }}>{d.form_type}</span>
                    )}
                    {d.date_filed && (
                      <span style={{ fontSize: 11, color: C.textDim }}>{d.date_filed}</span>
                    )}
                    {/* ⚠ C.orange, not C.amber — C.amber is #00FF87, emerald. */}
                    {Number.isFinite(Number(d.annual_value_usd)) && Number(d.annual_value_usd) > 0 && (
                      <span style={{ ...s.tag(C.orange), fontSize: 11, fontWeight: 700 }}>
                        ${Number(d.annual_value_usd).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {/* ⚠ Every one of these omits when absent. Provider was null on all
                      7 surveyed proxies, and executive/aircraft type are frequently
                      null even on a real hit — rendering "null" would look like a bug. */}
                  {(d.aircraft_type || d.provider || d.executive_name) && (
                    <div style={{ fontSize: 11, color: C.textMid, marginTop: 3 }}>
                      {[d.executive_name, d.aircraft_type, d.provider].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {d.disclosure_text && (
                    <div style={{
                      fontSize: 11, color: C.textDim, marginTop: 4,
                      fontStyle: "italic", lineHeight: 1.5,
                    }}>
                      &ldquo;{d.disclosure_text}&rdquo;
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                    {d.section && (
                      <span style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.06em" }}>
                        {String(d.section).toUpperCase()}
                      </span>
                    )}
                    {d.sec_url && (
                      <a
                        href={d.sec_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 12, color: C.blue, textDecoration: "none" }}
                      >
                        VIEW &rarr;
                      </a>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── SECTION 4 — EDGAR full-text search ────────────────────────── */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>FULL TEXT FILING SEARCH</span>
          <DataStatus status="live" source="" />
        </div>
        <div style={s.panelB}>
          <div style={{ fontSize: 13, color: C.textDim, marginBottom: 8 }}>
            Searches 10-K, 10-Q, 8-K, 13D, 13G, Form 4 filings from 2024+.
            Try company name, ticker, person name, or keyword.
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <input
              style={{ ...s.inp, flex: 1 }}
              placeholder='e.g. "Apple" or "NVDA" or "activist" or "material weakness"'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
            <button style={s.btn("amber")} onClick={search}>SEARCH</button>
          </div>

          {loading && (
            <div style={{ color: C.textDim, fontSize: 13, padding: "8px 0" }}>
              SEARCHING FILINGS...
            </div>
          )}
          {searchErr && (
            <div style={{ color: C.red, fontSize: 13, marginBottom: 8 }}>{searchErr}</div>
          )}

          {/* ⚠ Reads the NORMALISED shape from /api/edgar/search. The previous
              version read entity_name / period_of_report / file_path, none of
              which exist on an EDGAR hit — every row rendered a blank company, an
              undefined period and an "Archives/undefined" link. */}
          {results.map((r, i) => (
            <div key={i} style={{
              borderTop: `1px solid ${C.border}`,
              padding: "8px 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}>
              <div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 3, flexWrap: "wrap" }}>
                  <span style={{
                    ...s.tag(FORM_COLORS[r.form_type] || C.amber),
                    fontSize: 11,
                    fontWeight: 700,
                  }}>
                    {r.form_type || "—"}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                    {r.entity_name || "—"}
                  </span>
                  <span style={{ fontSize: 11, color: C.textDim }}>
                    Filed: {r.filed_date || "—"}
                  </span>
                  {r.period_of_report && (
                    <span style={{ fontSize: 11, color: C.textDim }}>
                      Period: {r.period_of_report}
                    </span>
                  )}
                </div>
              </div>
              {r.sec_url && (
                <a
                  href={r.sec_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 13, color: C.blue, textDecoration: "none", flexShrink: 0 }}
                >
                  View filing &#8599;
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── SECTION 5 — filing analyzer (unchanged) ───────────────────────── */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>FILING ANALYZER</span>
          <span style={{ ...s.tag(C.teal), fontSize: 11 }}>
            AI ANALYST · 4,000 CHAR LIMIT
          </span>
        </div>
        <div style={s.panelB}>
          <div style={{ fontSize: 13, color: C.textDim, marginBottom: 6 }}>
            Paste any section from a public filing — Risk Factors, MD&amp;A, footnotes,
            13D narrative, Form 4 remarks, earnings release language.
          </div>
          <div style={{ fontSize: 11, color: pasted.length > 3500 ? C.amber : C.textDim, marginBottom: 4 }}>
            {pasted.length}/4,000 characters
          </div>
          <textarea
            style={{
              ...s.inp,
              height: 110,
              resize: "vertical",
              marginBottom: 8,
            }}
            placeholder="Paste filing text here..."
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
          />
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              style={s.btn("amber")}
              onClick={analyze}
              disabled={analyzing || !pasted.trim()}
            >
              {analyzing ? "SYNTHESIZING..." : "SYNTHESIZE"}
            </button>
            {analysis && (
              <button
                style={s.btn()}
                onClick={() => setAnalysis("")}
              >
                CLEAR
              </button>
            )}
          </div>
          {analysis && (
            <div style={{ ...s.aiBox, marginTop: 10 }}>{analysis}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ALT DATA TAB
// ═══════════════════════════════════════════════════════════════════════════════

// ⚠⚠ NO LOCAL FALLBACK DATA (2026-10-27). REP_TANKERS, REP_FIRES, REP_IMPORTS,
// REP_CONGRESS, REP_CONTRACTS and REP_COURTS are gone. The panels hid them, but
// every fetch also pushed them onto the signal bus, where they fired "N VESSELS
// DARK" and "LARGE CONTRACT AWARDS" and reached the AI synthesis unmarked. A
// failed fetch, or a backend placeholder payload, now leaves the list empty and
// the panel shows its unavailable state.

// ⚠⚠ THE REGION LIST AND ITS COVERAGE STATE COME FROM THE ENDPOINT.
// /api/altdata/tankers returns a `regions` manifest alongside the vessel list:
//   { name, vessel_count, has_coverage, coverage_note, anchor_lat, anchor_lon }
// The hardcoded AIS_REGIONS array and AIS_NO_COVERAGE set that used to live here
// are gone — they were hand-synced copies of _AIS_CHOKEPOINTS and
// _AIS_NO_COVERAGE in app/services/altdata.py, which is now the only source.
// ⚠ has_coverage is a property of the RECEIVER NETWORK, not of today's count.
// Never re-derive it from vessel_count: a region can be quiet and still covered.

// ⚠ Census values are raw dollars — $265,407,066,272 for a month of total
// imports. Rendered unformatted they are unreadable, and the tab's job is
// comparison at a glance.
function tradeMoney(v) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${(n / 1e3).toFixed(1)}K`;
}

// ⚠⚠ null IS NOT ZERO. The backend returns null when there is no comparable
// base month, and "—" says that honestly; rendering 0.0% would assert the value
// held flat when it is simply unknown.
function TradeDelta({ pct, label }) {
  if (pct == null) {
    return <span style={{ fontSize: 11, color: C.textDim }}>{label}: —</span>;
  }
  const up = pct > 0;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: up ? C.green : pct < 0 ? C.red : C.textDim }}>
      {label}: {up ? "+" : ""}{pct.toFixed(1)}% {up ? "↑" : pct < 0 ? "↓" : ""}
    </span>
  );
}

// Filing-delay color coding (days between trade and disclosure).
function gapStyle(gap) {
  if (gap == null) return {};
  if (gap <= 14) return { color: C.green };
  if (gap <= 30) return { color: C.amber };
  if (gap <= 45) return { color: C.red };
  return { color: C.red, fontWeight: 700 };
}
// Trade side → "BUY" | "SELL" | "EXCHANGE" | null. The backend sends BUY / SELL /
// EXCHANGE / "—"; anything that is not clearly one of the three is null and shows
// no tag — never a guessed SELL.
const tradeSide = (t) => {
  const v = String(t || "");
  if (/buy|purchase/i.test(v)) return "BUY";
  if (/sell|sale|sold/i.test(v)) return "SELL";
  if (/exchange/i.test(v)) return "EXCHANGE";
  return null;
};
const SIDE_COLOR = { BUY: C.green, SELL: C.red, EXCHANGE: C.textDim };
const SIDE_VERB = { BUY: "purchased", SELL: "sold", EXCHANGE: "exchanged" };
function SideTag({ type, style }) {
  const side = tradeSide(type);
  if (!side) return null;
  return <span style={{ ...s.tag(SIDE_COLOR[side]), fontSize: 10, ...style }}>{side}</span>;
}

// ── Trade label: ticker, else asset name ──
// ⚠ A missing ticker reaches us in several spellings: null, "", whitespace, "--",
// "-", "—", "N/A". The backend recovers what it can from the asset text, but ~16%
// of scraped rows still arrive without one, most of them real securities whose
// symbol the House PDF parser truncated ("SP Applied Materials, Inc. - Common").
// Those rows show the asset name instead, styled quieter so the missing symbol is
// visible. A row with neither a ticker nor usable asset text is hidden.
const isBlankText = (v) =>
  typeof v !== "string" || /^[\s\p{P}\p{S}]*$/u.test(v) || /^\s*n\/?a\s*$/i.test(v);
function tradeLabel(t) {
  if (!isBlankText(t?.ticker)) return { text: t.ticker, missing: false };
  if (!isBlankText(t?.asset)) return { text: t.asset.trim().replace(/\s+/g, " "), missing: true };
  return null;
}

// ── Panel selector ────────────────────────────────────────────────────────────
// Manual refresh appends force_refresh so the backend deletes its cached key and
// re-fetches upstream. Auto-refresh omits it and serves the cache normally.
const forceParam = (force) => (force ? "?force_refresh=true" : "");

// A source is "unavailable" when its fetch failed ("offline") or it has no live
// rows — the CongressPanel showUnavailable gate, generalized.
const repUnavailable = (status, arr) => status === "offline" || !(Array.isArray(arr) && arr.length > 0);

// Shared UNAVAILABLE body state (CongressPanel pattern). Rendered in place of any
// representative/fallback rows so a panel's body always matches its red badge —
// fake data is never shown to the user.
function UnavailableState() {
  return (
    <div style={{
      background: "rgba(255,82,82,0.08)",
      border: "1px solid rgba(255,82,82,0.2)",
      borderRadius: 4, padding: "12px 16px",
    }}>
      <div style={{ color: "#FF5252", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em" }}>
        ● DATA TEMPORARILY UNAVAILABLE
      </div>
      <div style={{ color: C.textDim, fontSize: 10, marginTop: 4, lineHeight: 1.5 }}>
        This source is currently unreachable. Check back shortly.
      </div>
    </div>
  );
}

// ─── CRACK SPREADS ────────────────────────────────────────────────────────────
// The refiner's margin: what a barrel of crude is worth once it has been turned
// into product. Leads refinery utilisation, product inventories and refiner
// equity margins, none of which report in real time.
// ⚠ 15 minutes matches the Yahoo quote delay exactly. Polling faster re-fetches
// data that cannot have changed.
const CRACK_PATH = "/api/altdata/crack-spreads";
const CRACK_REFRESH_MS = 900000;

// ⚠⚠ MIRRORS _CRACK_REGIMES IN app/services/altdata.py. The backend owns the
// classification and returns `regime` as a string; this maps that string to a
// colour ONLY. Never re-derive the band from crack_321 here — two thresholds
// drifting apart would show a green STRONG label beside an amber number.
const CRACK_REGIME_COLOR = {
  "STRONG MARGINS": C.green,
  "NORMAL": C.textMid,
  "COMPRESSED": C.amber,
  // ⚠ STRESS has not occurred in the two years measured, and the band stays
  // anyway: a margin below $10/bbl means refiners are running at a loss and
  // cutting runs. A band that has not fired is not a band that is wrong.
  "STRESS": C.red,
};

// ⚠ Number(null) === 0, so a missing leg would render a confident "$0.00".
const crackNum = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

// ⚠ The payload timestamp is when the SERVICE computed the spread, and the panel
// serves a 900s cache — so this is "as of", never "now". Rendering it as live
// would present a quarter-hour-old margin as the current one.
const CRACK_ET_FMT = (() => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return null; }
})();
function crackEt(iso) {
  if (typeof iso !== "string" || !iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  try { return CRACK_ET_FMT ? `${CRACK_ET_FMT.format(d)} ET` : "—"; } catch { return "—"; }
}
const usd = (v, dp = 2) => {
  const n = crackNum(v);
  return n === null ? "—" : `$${n.toFixed(dp)}`;
};

const ALT_PANELS = [
  // ⚠ "MARITIME", not "OIL TANKERS". The feed is every hull in the monitored
  // boxes filtered to tankers and cargo — it was never tanker-only, and until
  // ShipStaticData was subscribed nothing had a type at all.
  { id: "TANKERS",         label: "MARITIME",         color: C.teal   },
  { id: "IMPORTS",         label: "US TRADE",         color: C.amber  },
  { id: "PETROLEUM",       label: "OIL INVENTORY",    color: C.green  },
  { id: "ENERGY",          label: "ENERGY",           color: C.blue   },
  { id: "THERMAL",         label: "THERMAL / FIRE",   color: C.orange },
];

// ── Unusual-options display helpers ──
const uoRatioColor = (r) => (r > 10 ? C.red : r > 5 ? C.orange : C.text);
// ⚠ HIDE-WHEN-NULL for the unusual-options rows: a field renders only when it holds
// a real number. null, undefined, NaN and strings are ABSENT — never "—", "$0" or
// "null". A real 0 is a number and still displays ("0", "0%", "0d"). Local on
// purpose: the shared fmtUsd prints "$0" for null (Number(value || 0)).
const uoReal = (v) => typeof v === "number" && Number.isFinite(v);
// Text fields render only when they are a non-empty string. A bare `{v && …}` is not
// enough: React prints a falsy NaN (and 0) as text.
const uoText = (v) => typeof v === "string" && v.length > 0;
const UO_URGENCY = {
  SAME_DAY:     { c: C.red,     l: "SAME DAY" },
  THIS_WEEK:    { c: C.orange,  l: "THIS WEEK" },
  THIS_MONTH:   { c: C.amber,   l: "THIS MONTH" },
  LONGER_DATED: { c: C.textDim, l: "LONGER" },
};
const uoSignalStyle = (signal) => {
  const base = String(signal).endsWith("CALL") ? C.green : C.red;
  const strong = String(signal).startsWith("EXTREME");
  const medium = String(signal).startsWith("STRONG");
  return {
    color: base,
    fontWeight: strong ? 700 : 600,
    opacity: strong ? 1 : medium ? 0.85 : 0.6,
    border: `1px solid ${base}${strong ? "" : "66"}`,
    borderRadius: 2,
    padding: "0 4px",
    fontSize: 9,
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
  };
};

// ── Congressional trades: party color tint + disclosed-size parsing ──
const partyTint = (party) =>
  party === "D" ? C.blue : party === "R" ? C.red : party === "I" ? C.purple : C.text;

// Parse a disclosed size string ("$1,001 - $15,000") into a { low, high } range.
function parseSizeRange(size) {
  const nums = String(size || "").replace(/[$,]/g, "").match(/\d+(?:\.\d+)?/g);
  if (!nums || !nums.length) return null;
  const vals = nums.map(Number);
  return { low: vals[0], high: vals[vals.length - 1] };
}

function fmtUSD(n) {
  if (n == null) return "—";
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}

// "1 Sep, 06:00" — the scraper's own delivery stamp, in the reader's locale.
// ⚠ Returns "" on anything unparseable so the caller renders nothing rather
// than "Invalid Date"; Number(null) === 0 has already cost this file four bugs
// and a bad date string is the same shape of trap.
function congressStamp(iso) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return "";
  try {
    return new Date(ms).toLocaleString(undefined, {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
}

// ── Grouped, searchable, sortable congressional-trades panel ──
// Level 1: one row per politician. Level 2 (expand): that member's trades.
function CongressPanel({ congress, keys, status, onRetry, onFollowMoney, freshness }) {
  const [search,   setSearch]   = useState("");
  const [sortMode, setSortMode] = useState("recent"); // recent | active | gap
  const [expanded, setExpanded] = useState(null);      // politician name; one open at a time

  // Unavailable-state tracking. "offline" status = the fetch failed or the backend
  // sent placeholder rows; either way there is nothing to show.
  // wasUnavailable stays sticky through a retry's "loading" phase so the banner
  // doesn't flicker; lastChecked stamps the time of the last settled fetch.
  const [lastChecked,    setLastChecked]    = useState(() => new Date());
  const [wasUnavailable, setWasUnavailable] = useState(false);
  useEffect(() => {
    if (status === "offline") { setWasUnavailable(true); setLastChecked(new Date()); }
    else if (status === "live") { setWasUnavailable(false); setLastChecked(new Date()); }
    // "loading" / undefined: leave both as-is
  }, [status]);
  const showUnavailable = status === "offline" || (status === "loading" && wasUnavailable);

  // ⚠⚠ THE ONE LIST. Every count, stat, group, sort, search and the analyst
  // context below reads `shown`, never `congress`, so the numbers always agree
  // with the rows on screen. Only rows with no ticker AND no asset text drop out.
  const shown = useMemo(() => congress.filter((c) => tradeLabel(c) !== null), [congress]);

  // Group trades by politician + per-member aggregates.
  const groups = useMemo(() => {
    const map = new Map();
    for (const c of shown) {
      const name = c.politician || "Unknown";
      if (!map.has(name)) {
        map.set(name, { name, party: c.party || "", chamber: c.chamber || "", state: c.state || "", trades: [] });
      }
      map.get(name).trades.push(c);
    }
    return [...map.values()].map((g) => {
      const dates = g.trades.map((t) => t.tradeDate).filter(Boolean).sort();
      const gaps  = g.trades.map((t) => t.reportingGap).filter((x) => typeof x === "number");
      let low = 0, high = 0, sized = false;
      for (const t of g.trades) {
        const r = parseSizeRange(t.size);
        if (r) { low += r.low; high += r.high; sized = true; }
      }
      return {
        ...g,
        count: g.trades.length,
        lastTrade: dates.length ? dates[dates.length - 1] : "",
        maxGap: gaps.length ? Math.max(...gaps) : -1,
        totalSize: sized ? `${fmtUSD(low)} – ${fmtUSD(high)}` : "—",
      };
    });
  }, [shown]);

  // Search: match politician name OR any traded ticker (the asset name stands in
  // for the ticker on rows that have none).
  const q = search.trim().toLowerCase();
  const filtered = q
    ? groups.filter((g) =>
        g.name.toLowerCase().includes(q) ||
        g.trades.some((t) => tradeLabel(t).text.toLowerCase().includes(q)))
    : groups;

  const sorted = [...filtered].sort((a, b) => {
    if (sortMode === "active") return b.count - a.count;
    if (sortMode === "gap")    return b.maxGap - a.maxGap;
    return String(b.lastTrade).localeCompare(String(a.lastTrade)); // recent (default)
  });

  // Summary stats + AI analyst context (context shape unchanged).
  const gapsAll = shown.map((c) => c.reportingGap).filter((x) => typeof x === "number");
  const avgGap  = gapsAll.length ? Math.round(gapsAll.reduce((s, g) => s + g, 0) / gapsAll.length) : 0;
  const lateCount = gapsAll.filter((g) => g > 30).length;
  const analystSorted = [...shown].sort((a, b) =>
    String(b.tradeDate || "").localeCompare(String(a.tradeDate || "")));
  const longest = shown.reduce(
    (m, c) => (typeof c.reportingGap === "number" && c.reportingGap > (m?.reportingGap ?? -1) ? c : m),
    null,
  );

  const SORTS = [["recent", "RECENT"], ["active", "MOST ACTIVE"], ["gap", "LONGEST GAP"]];

  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={s.panelT}>STOCK ACT DISCLOSURES</span>
        <DataStatus status={status || "offline"} source="STOCK Act" />
      </div>
      <div style={s.panelB}>
        {/* ⚠⚠ DATA AGE, NOT FETCH TIME. `lastChecked` below stamps when WE last
            asked; this stamps when the scraper last DELIVERED. A feed can be
            fetched successfully every 30 seconds and still be three days old.
            ⚠ Rendered only when the backend actually sent data_freshness — it
            omits the key on representative data, and the panel prints nothing
            rather than claiming an age for rows that were never fetched. */}
        {freshness && !showUnavailable && (
          <div style={{
            fontSize: 10, marginBottom: 8,
            color: freshness.is_stale ? C.amber : C.textDim,
          }}>
            Data as of {congressStamp(freshness.last_updated)} ·{" "}
            {Math.round(freshness.hours_ago)}h ago{freshness.is_stale ? " ⚠" : ""}
          </div>
        )}
        {showUnavailable ? (
          <div>
            <div style={{
              background: "rgba(255,82,82,0.08)",
              border: "1px solid rgba(255,82,82,0.2)",
              borderRadius: 4, padding: "12px 16px", marginBottom: 12,
            }}>
              <div style={{ color: "#FF5252", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em" }}>
                ● DATA TEMPORARILY UNAVAILABLE
              </div>
              <div style={{ color: C.textDim, fontSize: 10, marginTop: 4, lineHeight: 1.5 }}>
                Congressional disclosure sources are currently unreachable. Data will populate automatically when restored.
              </div>
              <div style={{ color: C.textDim, fontSize: 9, marginTop: 4 }}>
                Last checked: {lastChecked.toLocaleTimeString()}
              </div>
            </div>
            <button
              onClick={onRetry}
              disabled={status === "loading"}
              style={{ ...s.btn("teal"), minHeight: 44, padding: "8px 12px", fontSize: 11 }}
            >
              <RefreshCw size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
              {status === "loading" ? "RETRYING…" : "RETRY"}
            </button>
          </div>
        ) : (
          <>
        {/* Summary stats */}
        <div style={{ fontSize: 10, color: C.textDim, marginBottom: 8, lineHeight: 1.6 }}>
          {shown.length} TRADES · {groups.length} {groups.length === 1 ? "POLITICIAN" : "POLITICIANS"} ·{" "}
          AVG GAP: {avgGap} DAYS ·{" "}
          <span style={{ color: lateCount > 0 ? C.red : C.textDim, fontWeight: lateCount > 0 ? 700 : 400 }}>
            {lateCount} FILED &gt;30 DAYS LATE
          </span>
        </div>

        {/* Search */}
        <div style={{ position: "relative", marginBottom: 8 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or ticker..."
            style={{
              width: "100%", boxSizing: "border-box", minHeight: 44,
              background: C.panel, border: `1px solid ${C.border}`, color: C.text,
              fontFamily: font, fontSize: 13, padding: "8px 34px 8px 10px", borderRadius: 3,
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              title="Clear"
              style={{
                position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", color: C.textDim, cursor: "pointer",
                fontSize: 18, lineHeight: 1, padding: "0 6px",
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* Sort controls */}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
          {SORTS.map(([mode, label]) => (
            <button
              key={mode}
              onClick={() => setSortMode(mode)}
              style={{ ...s.btn(sortMode === mode ? "amber" : "default"), minHeight: 44, padding: "8px 12px", fontSize: 11 }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Filing-delay color legend (preserved) */}
        <div style={{ fontSize: 9, color: C.textDim, marginBottom: 8 }}>
          Filing delay:{" "}
          <span style={{ color: C.green }}>●</span> &lt;15 days{"   "}
          <span style={{ color: C.amber }}>●</span> 15-30 days{"   "}
          <span style={{ color: C.red }}>●</span> &gt;30 days
        </div>

        <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10, lineHeight: 1.65 }}>
          <span style={{ color: C.red, fontWeight: 700 }}>SIGNAL MECHANIC:</span>{" "}
          STOCK Act (2012) requires congressional trades to be disclosed within 45 days.
          The trades already happened — the disclosure is lagged intelligence.
          Cross-reference against committee assignments for highest-signal reads.
        </div>

        {/* Level 1 — politician rows */}
        {sorted.length === 0 ? (
          <div style={{ fontSize: 12, color: C.textDim, padding: "12px 4px" }}>
            {congress.length === 0 ? "No disclosures loaded."
              : shown.length === 0 ? "No disclosures with a ticker or asset name."
              : "No matches."}
          </div>
        ) : (
          sorted.map((g) => {
            const isOpen = expanded === g.name;
            return (
              <div key={g.name} style={{ borderBottom: `1px solid ${C.border}` }}>
                <div
                  onClick={() => setExpanded(isOpen ? null : g.name)}
                  style={{
                    minHeight: 48, padding: "8px 6px", cursor: "pointer",
                    display: "flex", flexDirection: "column", justifyContent: "center", gap: 3,
                    background: isOpen ? C.panelAlt : "transparent",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: partyTint(g.party) }}>{g.name}</span>
                    {["D", "R", "I"].includes(g.party) && (
                      <span style={{ ...s.tag(partyTint(g.party)), fontSize: 10 }}>{g.party}</span>
                    )}
                    {g.chamber && (
                      <span style={{ fontSize: 10, color: C.textDim, letterSpacing: "0.08em", fontWeight: 700 }}>
                        {g.chamber.toUpperCase()}
                      </span>
                    )}
                    {g.state && <span style={{ fontSize: 10, color: C.textDim }}>{g.state}</span>}
                    <span style={{ marginLeft: "auto", color: C.textDim, fontSize: 13 }}>{isOpen ? "▼" : "▶"}</span>
                  </div>
                  <div style={{ fontSize: 10, color: C.textDim }}>
                    {g.count} {g.count === 1 ? "trade" : "trades"} · last {g.lastTrade || "—"} · {g.totalSize}
                  </div>
                </div>

                {/* Level 2 — trade detail (stacks vertically for narrow viewports) */}
                {isOpen && (
                  <div style={{ paddingLeft: 14 }}>
                    {g.trades.map((t, i) => {
                      const label = tradeLabel(t);
                      return (
                      <div
                        key={i}
                        style={{
                          minHeight: 44, padding: "8px 6px",
                          borderTop: `1px solid ${C.border}`,
                          display: "flex", flexDirection: "column", gap: 3,
                        }}
                      >
                        {label.missing ? (
                          // No ticker: the asset name in its place, quieter, one line
                          // with an ellipsis so a long name never pushes BUY/SELL off
                          // a phone screen. The full name is on hover / long-press.
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <span
                              title={`${label.text} (no ticker in the filing)`}
                              style={{
                                color: C.textMid, fontSize: 12, fontStyle: "italic", fontWeight: 400,
                                minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              }}
                            >
                              {label.text}
                            </span>
                            <SideTag type={t.type} style={{ flexShrink: 0 }} />
                          </div>
                        ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 700, color: C.amber, fontSize: 13 }}>{t.ticker}</span>
                          <SideTag type={t.type} />
                        </div>
                        )}
                        <div style={{ fontSize: 10, color: C.textDim }}>
                          Traded {t.tradeDate || "—"}{t.filedDate ? ` · Filed ${t.filedDate}` : ""}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 11 }}>
                          <span style={{ color: C.textMid }}>{t.size || "—"}</span>
                          {t.reportingGap != null && (
                            <span style={{ fontSize: 10, ...gapStyle(t.reportingGap) }}>
                              {t.reportingGap > 45 ? "⚠ " : ""}{t.reportingGap} days to file
                            </span>
                          )}
                        </div>
                      </div>
                      );
                    })}

                    {/* Deep-link into the Follow the Money network for this member.
                        Only rendered when the parent wires the callback (App passes
                        it via CongressionalTab); no-op panel elsewhere. */}
                    {onFollowMoney && (
                      <div style={{ borderTop: `1px solid ${C.border}`, padding: "10px 6px" }}>
                        <button
                          onClick={() => onFollowMoney(g.name)}
                          title={`Build the Follow the Money network for ${g.name}`}
                          style={{ ...s.btn("teal"), minHeight: 44, padding: "8px 12px", fontSize: 11 }}
                        >
                          ⚡ FOLLOW THE MONEY
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* AI analyst button (preserved) */}
        {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
          <div style={{ marginTop: 10 }}>
            <AnalystButton
              panel="congress"
              label="SYNTHESIZE"
              context={
                `Congressional STOCK Act disclosures:\n` +
                analystSorted.map((c) =>
                  `${c.politician} (${c.chamber}): ${SIDE_VERB[tradeSide(c.type)] || "traded"} ` +
                  `${tradeLabel(c).text}, ${c.size}, traded ${c.tradeDate}, filed ${c.filedDate}` +
                  `${c.reportingGap != null ? ` (${c.reportingGap} days later)` : ""}`
                ).join("\n") +
                `\n\nReporting gap summary:\n` +
                `Average reporting gap: ${avgGap} days\n` +
                `Trades filed > 30 days late: ${lateCount}\n` +
                `Longest gap: ${longest ? `${longest.reportingGap} days (${longest.politician}, ${tradeLabel(longest).text})` : "n/a"}\n\n` +
                `Analyze sector concentration of purchases, ` +
                `which tickers appear across multiple members, ` +
                `and what committee assignments might be relevant to the disclosed trades. ` +
                `Note unusually large reporting gaps as a potential information-advantage signal.`
              }
            />
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

export function AltDataTab({ keys, onData }) {
  const [active,     setActive]    = useState("TANKERS");
  const [tankers,    setTankers]   = useState([]);
  const [aisRegions, setAisRegions] = useState([]);
  // ⚠ An OBJECT, not a list — the Census payload carries three sections plus
  // its period. null means "not fetched yet"; a fetch failure leaves it null so
  // the panel shows its error state rather than fabricated rows.
  const [trade,      setTrade]     = useState(null);
  // ⚠ Mirrors the tape component's listener below — same 640px breakpoint, same
  // shape. Declared separately because that state belongs to a different
  // component and is not in scope here.
  const [tradeNarrow, setTradeNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
  );
  const [petroleum,  setPetroleum] = useState([]);
  const [crack,      setCrack]     = useState(null);
  const [fires,      setFires]     = useState([]);
  const [statusMap,  setStatusMap] = useState({});

  const setStatus = (k, v) => setStatusMap((p) => ({ ...p, [k]: v }));


  // ── Crack spreads ──
  // ⚠ No representative fallback and no empty shape: the backend raises unless
  // all three futures legs resolve, and serve_cached then returns the last good
  // copy. A null here means the request itself failed, which the panel says
  // rather than rendering a zero margin.
  const fetchCrack = useCallback(async (forceRefresh = false) => {
    setStatus("crack", "loading");
    try {
      const res = await backendFetch(
        "https://query1.finance.yahoo.com",
        CRACK_PATH + forceParam(forceRefresh)
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const d = await res.json();
      if (d && crackNum(d.crack_321) !== null) {
        setCrack(d);
        setStatus("crack", "live");
      } else throw new Error("empty");
    } catch {
      setCrack(null);
      setStatus("crack", "offline");
    }
  }, []);

  // ── Petroleum — EIA weekly inventory (no fallback) ──
  const fetchPetroleum = useCallback(async (forceRefresh = false) => {
    setStatus("petroleum", "loading");
    try {
      const key = keys.eiaKey || "DEMO_KEY";
      const res = await backendFetch(
        `${EIA_BASE}/petroleum/stoc/wstk/data/` +
        `?frequency=weekly&data[0]=value` +
        `&facets[product][]=EPC0&facets[duoarea][]=NUS` +
        `&sort[0][column]=period&sort[0][direction]=desc` +
        `&length=20&api_key=${key}`,
        `/api/eia/petroleum` + forceParam(forceRefresh)
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      if (data.observations?.length) {
        const d = data.observations
          .map((o) => ({ date: o.date, value: parseFloat(o.value) / 1000 }))
          .reverse();
        setPetroleum(d);
        if (onData) onData("petroleum", d);
        setStatus("petroleum", keys.eiaKey ? "live" : "delayed");
      } else throw new Error("no data");
    } catch {
      // ⚠⚠ No stand-in series. Twenty hardcoded weekly values used to sit here;
      // they drew a chart, fired a STRONG "CRUDE INVENTORY DRAW" and went to the
      // AI as real inventory data. Nothing reaches the bus on failure.
      setPetroleum([]);
      setStatus("petroleum", "offline");
    }
  }, [keys.eiaKey, onData]);

  // ── NASA FIRMS thermal anomalies ──
  const fetchFires = useCallback(async (forceRefresh = false) => {
    setStatus("thermal", "loading");
    try {
      const res = await backendFetch(
        "https://firms.modaps.eosdis.nasa.gov/api/area/csv/VIIRS_SNPP_NRT/world/1",
        "/api/altdata/thermal" + forceParam(forceRefresh)
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const parsed = liveRows(await res.json());
      if (parsed.length) {
        setFires(parsed);
        if (onData) onData("fires", parsed);
        setStatus("thermal", "live");
      } else throw new Error("empty");
    } catch {
      setFires([]);
      setStatus("thermal", "offline");
    }
  }, [onData]);

  // ── Tankers — VesselFinder AIS (via backend proxy, no fallback) ──
  const fetchTankers = useCallback(async (forceRefresh = false) => {
    setStatus("tankers", "loading");
    try {
      const res = await backendFetch(
        "https://www.vesselfinder.com",
        "/api/altdata/tankers" + forceParam(forceRefresh)
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      const list = liveRows(data);
      // ⚠ Set BEFORE the length gate — the manifest is present on every payload
      // shape including the failure ones, and the coverage strip must render even
      // when the vessel list is empty.
      setAisRegions(Array.isArray(data?.regions) ? data.regions : []);
      // ⚠ The bus is fed only after the placeholder check. It used to be fed
      // first, so stand-in vessels fired "N VESSELS DARK" behind a hidden panel.
      if (list.length) {
        setTankers(list);
        if (onData) onData("tankers", list);
        setStatus("tankers", "live");
      } else throw new Error("empty");
    } catch {
      setTankers([]);
      setStatus("tankers", "offline");
    }
  }, [onData]);

  // ── Imports — U.S. Customs Bills of Lading (via backend proxy) ──
  // ⚠⚠ NO REPRESENTATIVE FALLBACK. This tab used to answer an outage with five
  // hardcoded rows claiming WALMART took 847 containers from YANTIAN, badged
  // "mock" in a corner most readers never look at. Census data is either real or
  // absent; an honest error state is the only other option.
  const fetchImports = useCallback(async (forceRefresh = false) => {
    setStatus("imports", "loading");
    try {
      const res = await backendFetch(
        "https://api.census.gov",
        "/api/altdata/imports" + forceParam(forceRefresh)
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      if (!data || !Array.isArray(data.top_categories) || !data.top_categories.length) {
        throw new Error("empty");
      }
      setTrade(data);
      setStatus("imports", "live");
    } catch {
      setTrade(null);
      setStatus("imports", "offline");
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const h = (e) => setTradeNarrow(e.matches);
    mq.addEventListener?.("change", h);
    return () => mq.removeEventListener?.("change", h);
  }, []);

  // ── Load panel data on switch ──
  useEffect(() => {
    // Tankers, imports now route through the backend proxy. A failed fetch
    // leaves its list empty, so switching back to the panel retries it.
    if (tankers.length  === 0) fetchTankers();
    if (!trade) fetchImports();

    if (active === "PETROLEUM" && petroleum.length === 0) fetchPetroleum();
    if (active === "ENERGY" && crack === null) fetchCrack();
    if (active === "THERMAL"   && fires.length     === 0) fetchFires();
  }, [active]);


  const typeColors = { Industrial: C.amber, Agricultural: C.green, Wildfire: C.red, Conflict: C.orange, Detected: C.textDim };

  // Manual refresh — force a fresh upstream fetch for the active panel.
  const refreshing = statusMap[active.toLowerCase()] === "loading";
  // ⚠ Polls only while the ENERGY panel is OPEN. A background timer on a hidden
  // panel is a request every 15 minutes for a tab nobody is looking at, and the
  // other panels here are all fetch-on-switch for exactly that reason.
  useEffect(() => {
    if (active !== "ENERGY") return undefined;
    const id = setInterval(() => fetchCrack(), CRACK_REFRESH_MS);
    return () => clearInterval(id);
  }, [active, fetchCrack]);

  const handleRefresh = () => {
    if (active === "TANKERS")        fetchTankers(true);
    else if (active === "IMPORTS")   fetchImports(true);
    else if (active === "PETROLEUM") fetchPetroleum(true);
    else if (active === "ENERGY") fetchCrack(true);
    else if (active === "THERMAL")   fetchFires(true);
  };

  return (
    <div>
      {/* Panel selector */}
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
        {ALT_PANELS.map((p) => (
          <button
            key={p.id}
            onClick={() => setActive(p.id)}
            style={{ ...s.btn(active === p.id ? "amber" : "default"), padding: "4px 10px" }}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh — fetch fresh data now"
          style={{ ...s.btn("teal"), padding: "4px 10px", marginLeft: "auto" }}
        >
          <RefreshCw size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
          {refreshing ? "REFRESHING..." : "REFRESH"}
        </button>
      </div>

      {/* ── TANKERS ── */}
      {active === "TANKERS" && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>VESSEL TRACKING — TANKERS &amp; CARGO</span>
            <DataStatus status={statusMap.tankers || "offline"} source="Vessel Tracking" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10, lineHeight: 1.65 }}>
              <span style={{ color: C.teal, fontWeight: 700 }}>SIGNAL MECHANIC:</span>{" "}
              Vessels going dark (transponder off) near sanctioned ports, loitering offshore (floating storage),
              or making unexpected course changes are leading indicators of crude flow shifts —
              visible days to weeks before official inventory reports.
            </div>

            {/* ⚠⚠ REGION COVERAGE, STATED. Every monitored region is listed whether
                or not it returned vessels, because an absent region is otherwise
                indistinguishable from a quiet one. Regions in AIS_NO_COVERAGE are
                labelled as a RECEIVER gap rather than a traffic observation — we
                are not watching Hormuz, and the panel must not imply that we are. */}
            {!repUnavailable(statusMap.tankers, tankers) && (() => {
              const counts = {};
              tankers.forEach((v) => {
                const r = v.region || "Unnamed area";
                counts[r] = (counts[r] || 0) + 1;
              });
              return (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
                  {aisRegions.map((reg) => {
                    const n = counts[reg.name] || 0;
                    // ⚠ has_coverage from the manifest. The old test was
                    // `n === 0 && AIS_NO_COVERAGE.has(r)` — which would have
                    // stopped flagging a dead region the moment one stray vessel
                    // appeared in it, and flagged nothing at all if the hardcoded
                    // set ever drifted from the backend's.
                    const dead = reg.has_coverage === false;
                    const color = dead ? C.red : n > 0 ? C.teal : C.textDim;
                    return (
                      <span key={reg.name} style={{ ...s.tag(color), fontSize: 10 }}>
                        {reg.name}: {dead ? (reg.coverage_note || "no receiver coverage") : n}
                      </span>
                    );
                  })}
                </div>
              );
            })()}

            {repUnavailable(statusMap.tankers, tankers) ? <UnavailableState /> : tankers.map((v, i) => (
              <div key={v.mmsi || i} style={{
                ...s.card(v.aisDark ? C.red : C.teal),
                borderLeft: `3px solid ${v.aisDark ? C.red : v.speed < 0.5 ? C.amber : C.teal}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <div>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{v.name}</span>
                    {/* ⚠ The type is only real once ShipStaticData has been seen for
                        this hull. Until then it is the "Vessel" placeholder, and
                        saying so is the difference between "we know it is a tanker"
                        and "we have not been told yet". */}
                    {v.has_static ? (
                      <span style={{ ...s.tag(C.teal), fontSize: 10, marginLeft: 8 }}>{v.type}</span>
                    ) : (
                      <span style={{ fontSize: 11, color: C.textDim, marginLeft: 8 }}>
                        type pending
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: C.textDim, marginLeft: 6 }}>FLAG: {v.flag}</span>
                    {v.region && (
                      <span style={{ fontSize: 11, color: C.textDim, marginLeft: 6 }}>{v.region}</span>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {v.aisDark
                      ? <span style={{ ...s.tag(C.red), fontSize: 11 }}>⚠ DARK VESSEL</span>
                      : v.speed < 0.5
                      ? <span style={{ ...s.tag(C.amber), fontSize: 11 }}>⚓ STATIONARY</span>
                      : <span style={{ fontSize: 13, color: C.green }}>{v.speed}kn</span>
                    }
                    {v.dest && v.dest !== "UNKNOWN" && (
                      <div style={{ fontSize: 11, color: C.teal }}>→ {v.dest}</div>
                    )}
                    {v.is_cached && (
                      <div style={{ fontSize: 9, color: C.textDim }}>last known</div>
                    )}
                  </div>
                </div>
                {v.signal && (
                  <div style={{ fontSize: 13, color: C.teal, background: C.tealBg, borderRadius: 2, padding: "3px 7px" }}>
                    📡 {v.signal}
                  </div>
                )}
              </div>
            ))}
            {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && !repUnavailable(statusMap.tankers, tankers) && (
              <AnalystButton
                panel="tankers"
                label="SYNTHESIZE"
                context={
                  `AIS fleet snapshot — ${tankers.length} vessels monitored.\n` +
                  `Dark vessels: ${tankers.filter((v) => v.aisDark).length}\n` +
                  `Stationary: ${tankers.filter((v) => !v.aisDark && v.speed < 0.5).length}\n` +
                  `Underway: ${tankers.filter((v) => v.speed > 1).length}\n` +
                  // ⚠ The model is told how many hulls are actually TYPED. Without
                  // it, "12 tankers" and "12 vessels of unknown type" read the same,
                  // and the second is not evidence of a crude flow.
                  `Confirmed vessel type (ShipStaticData): ${tankers.filter((v) => v.has_static).length}\n` +
                  // ⚠ Regions with no receiver coverage are named as such, so the
                  // synthesis cannot read an absent chokepoint as a quiet one.
                  `No receiver coverage: ${aisRegions.filter((r) => r.has_coverage === false).map((r) => r.name).join(", ") || "none"}\n\n` +
                  tankers.slice(0, 40).map((v) =>
                    `${v.name} (${v.has_static ? v.type : "type unconfirmed"}): ` +
                    `${v.aisDark ? "AIS DARK" : v.speed < 0.5 ? "stationary" : `${v.speed}kn`}` +
                    `${v.dest && v.dest !== "UNKNOWN" ? ` → ${v.dest}` : ""}` +
                    `${v.region ? ` [${v.region}]` : ""} — ${v.signal}`
                  ).join("\n") +
                  `\n\nAnalyze what this fleet positioning implies about crude oil flows, ` +
                  `potential sanctioned cargo movements, floating storage levels, ` +
                  `and what leading supply signals the AIS pattern suggests.`
                }
              />
            )}
          </div>
        </div>
      )}

      {/* ── US TRADE ── */}
      {/* ⚠⚠ THIS PANEL REPLACED A FABRICATION. It rendered five hardcoded rows —
          "WALMART · YANTIAN · 847 containers" — because company-level bills of
          lading need a paid provider the platform does not have. Census publishes
          NATIONAL AGGREGATES by commodity and country: no company dimension, no
          container counts. The copy below says what the data actually is. */}
      {active === "IMPORTS" && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>US TRADE INTELLIGENCE</span>
            <DataStatus
              status={statusMap.imports || "loading"}
              source={`${trade?.data_period ? `${trade.data_period}` : ""}`}
            />
          </div>
          <div style={s.panelB}>
            {!trade ? (
              /* ⚠ An honest empty state. No cards, no placeholder rows — the
                 previous version's failure mode was showing invented data that
                 looked exactly like the real thing. */
              <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.7, padding: "8px 0" }}>
                US Trade Intelligence is temporarily unavailable. Data updates
                monthly — check back shortly.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, color: C.textDim, marginBottom: 10, lineHeight: 1.6 }}>
                  {trade.data_period} data · Updated monthly
                  <div>{trade.data_lag_note}.</div>
                </div>

                {/* ── SECTION 1 — top categories ── */}
                <div style={{ fontSize: 11, color: C.amber, fontWeight: 700,
                              letterSpacing: "0.1em", marginBottom: 2 }}>
                  TOP IMPORT CATEGORIES
                </div>
                <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8 }}>
                  By value, {trade.data_period}
                </div>
                {(trade.top_categories || []).map((c) => (
                  <div key={c.hs_code} style={{ ...s.card(), display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center", gap: 10,
                                                flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 190 }}>
                      <span style={{ fontSize: 10, color: C.textDim }}>HS{c.hs_code}</span>{" "}
                      <span style={{ fontSize: 13, color: C.text }}>{c.description}</span>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.amber,
                                  minWidth: 78, textAlign: "right" }}>
                      {tradeMoney(c.value_current)}
                    </div>
                    <div style={{ display: "flex", gap: 10, minWidth: 190,
                                  justifyContent: "flex-end" }}>
                      <TradeDelta pct={c.mom_pct} label="MoM" />
                      <TradeDelta pct={c.yoy_pct} label="YoY" />
                    </div>
                  </div>
                ))}

                {/* ── SECTION 2 — origins ── */}
                <div style={{ fontSize: 11, color: C.amber, fontWeight: 700,
                              letterSpacing: "0.1em", margin: "16px 0 2px" }}>
                  IMPORT ORIGINS
                </div>
                <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8 }}>
                  Top source countries, {trade.data_period}
                </div>
                {(() => {
                  /* ⚠ The bar is scaled to the LARGEST COUNTRY IN THIS LIST, not
                     to total imports. The top ten are a fraction of the total, so
                     scaling to the total would render every bar as a sliver and
                     show nothing. */
                  const rows = trade.top_countries || [];
                  const max = rows.reduce((m, r) => Math.max(m, Number(r.value_current) || 0), 0) || 1;
                  return rows.map((r) => (
                    <div key={r.country} style={{ ...s.card() }}>
                      <div style={{ display: "flex", justifyContent: "space-between",
                                    alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, color: C.text, flex: 1, minWidth: 150 }}>
                          {r.country}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: C.amber }}>
                          {tradeMoney(r.value_current)}
                        </span>
                        <span style={{ minWidth: 96, textAlign: "right" }}>
                          <TradeDelta pct={r.mom_pct} label="MoM" />
                        </span>
                      </div>
                      <div style={{ height: 3, background: C.border, borderRadius: 2, marginTop: 5 }}>
                        <div style={{
                          height: 3, borderRadius: 2, background: C.amber,
                          width: `${Math.max(1, ((Number(r.value_current) || 0) / max) * 100)}%`,
                        }} />
                      </div>
                    </div>
                  ));
                })()}

                {/* ── SECTION 3 — strategic commodities ── */}
                <div style={{ fontSize: 11, color: C.amber, fontWeight: 700,
                              letterSpacing: "0.1em", margin: "16px 0 2px" }}>
                  STRATEGIC COMMODITIES
                </div>
                <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8 }}>
                  Key supply chain indicators
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: tradeNarrow ? "1fr" : "1fr 1fr",
                  gap: 10, alignItems: "start",
                }}>
                  {(trade.commodity_watch || []).map((c) => (
                    <div key={c.hs_code} style={{
                      ...s.card(), marginBottom: 0,
                      /* ⚠ Border reflects MoM direction; C.textDim when the change
                         is unknown, which is a third state and not "flat". */
                      borderLeft: `3px solid ${
                        c.mom_pct == null ? C.textDim : c.mom_pct > 0 ? C.green : C.red}`,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between",
                                    alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        <div>
                          <span style={{ fontSize: 10, color: C.textDim }}>HS{c.hs_code}</span>{" "}
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                            {c.description}
                          </span>
                        </div>
                        <span style={{ fontSize: 15, fontWeight: 700, color: C.amber }}>
                          {tradeMoney(c.value_current)}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
                        <TradeDelta pct={c.mom_pct} label="MoM" />
                        <TradeDelta pct={c.yoy_pct} label="YoY" />
                      </div>
                      {(c.top_3_countries || []).length > 0 && (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                          {c.top_3_countries.map((t) => (
                            <span key={t.country} style={{ ...s.tag(C.textMid), fontSize: 9 }}>
                              {t.country} {tradeMoney(t.value)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
                  <AnalystButton
                    panel="signal_board"
                    label="SYNTHESIZE"
                    context={
                      `US Census import statistics for ${trade.data_period}:\\n` +
                      `Top categories: ` +
                      (trade.top_categories || []).map((c) =>
                        `${c.description} ${tradeMoney(c.value_current)} (MoM ${c.mom_pct ?? "n/a"}%, YoY ${c.yoy_pct ?? "n/a"}%)`
                      ).join("; ") +
                      `\\nTop origins: ` +
                      (trade.top_countries || []).map((c) =>
                        `${c.country} ${tradeMoney(c.value_current)} (MoM ${c.mom_pct ?? "n/a"}%)`
                      ).join("; ") +
                      `\\nStrategic commodities: ` +
                      (trade.commodity_watch || []).map((c) =>
                        `${c.description} ${tradeMoney(c.value_current)} (MoM ${c.mom_pct ?? "n/a"}%)`
                      ).join("; ") +
                      `\\n\\nThese are NATIONAL AGGREGATES by commodity and country — not ` +
                      `company-level shipments. Analyse what the month-over-month and ` +
                      `year-over-year moves suggest about supply chains, input costs and ` +
                      `demand. ${trade.data_lag_note}, so treat this as a confirming ` +
                      `indicator rather than a leading one.`
                    }
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── ENERGY: refinery crack spreads ── */}
      {active === "ENERGY" && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>REFINERY CRACK SPREADS</span>
            <DataStatus status={statusMap.crack || "loading"} source="" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10, lineHeight: 1.65 }}>
              <span style={{ color: C.blue, fontWeight: 700 }}>SIGNAL MECHANIC:</span>{" "}
              The crack spread is the refiner&apos;s margin — what a barrel of crude is worth
              once it has been turned into product. Widening margins pull refinery runs up
              and draw crude out of storage; compression does the opposite, weeks before
              inventory reports show it.
            </div>

            {!crack ? (
              <div style={{ fontSize: 11, color: C.textDim, padding: 12 }}>
                {statusMap.crack === "loading"
                  ? "LOADING CRACK SPREADS…"
                  : "Crack spreads unavailable — futures quotes could not be reached."}
              </div>
            ) : (
              <>
                {/* ROW 1 — the raw legs. ⚠ UNITS ARE LABELLED ON EVERY TILE:
                    crude is per BARREL and the two products are per GALLON, and
                    that difference is exactly what makes a naive subtraction of
                    these three numbers meaningless. */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  {[
                    { k: "WTI CRUDE", v: usd(crack.cl_price), u: "/bbl" },
                    { k: "RBOB GASOLINE", v: usd(crack.rb_price, 4), u: "/gal" },
                    { k: "HEATING OIL", v: usd(crack.ho_price, 4), u: "/gal" },
                  ].map((t) => (
                    <div key={t.k} style={{ ...s.card(), flex: "1 1 140px", marginBottom: 0 }}>
                      <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.08em" }}>{t.k}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>
                        {t.v}
                        <span style={{ fontSize: 10, color: C.textDim, fontWeight: 400 }}>{t.u}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ROW 2 — the margins. The 3-2-1 is the headline and is sized as such. */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  {[
                    { k: "GASOLINE CRACK", v: crack.gasoline_crack, big: false },
                    { k: "DISTILLATE CRACK", v: crack.distillate_crack, big: false },
                    { k: "3-2-1 CRACK SPREAD", v: crack.crack_321, big: true },
                  ].map((t) => (
                    <div
                      key={t.k}
                      style={{
                        ...s.card(t.big ? C.blue : undefined),
                        flex: t.big ? "2 1 220px" : "1 1 150px",
                        marginBottom: 0,
                      }}
                    >
                      <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.08em" }}>{t.k}</div>
                      <div style={{
                        fontSize: t.big ? 30 : 20, fontWeight: 700, lineHeight: 1.2,
                        // ⚠ Colour tracks the SIGN, not the regime — a negative
                        // crack is a refinery running at a loss regardless of band.
                        color: crackNum(t.v) === null ? C.textDim : t.v < 0 ? C.red : C.text,
                      }}>
                        {usd(t.v)}
                        <span style={{ fontSize: 11, color: C.textDim, fontWeight: 400 }}>/bbl</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* REGIME — the backend's label, coloured here and nothing more. */}
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <span style={{
                    fontSize: 15, fontWeight: 700, letterSpacing: "0.1em",
                    color: CRACK_REGIME_COLOR[crack.regime] || C.textMid,
                  }}>
                    {crack.regime || "—"}
                  </span>
                  {/* ⚠⚠ THE PERCENTILE CONTEXT IS THE POINT OF THIS LINE. A reader
                      seeing "STRONG MARGINS" with no reference cannot tell a good
                      quarter from a two-year extreme. The current print sits near
                      the top of the measured range and must say so. */}
                  {crack.percentile_context && crack.percentile_context !== "Normal range" && (
                    <span style={{
                      ...s.tag(crack.percentile_context === "Historically stressed" ? C.red : C.amber),
                      fontSize: 10,
                    }}>
                      {crack.percentile_context}
                    </span>
                  )}
                </div>

                {/* BENCHMARK + provenance. ⚠ The median is a MEASURED reference
                    (504 aligned sessions), not a round number — it is what makes
                    the headline figure legible at a glance. */}
                <div style={{ fontSize: 10, color: C.textDim, marginTop: 6, lineHeight: 1.6 }}>
                  2-year median: {usd(crack.benchmark_median)}/bbl
                  {crack.timestamp ? ` · as of ${crackEt(crack.timestamp)}` : ""}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── PETROLEUM ── */}
      {active === "PETROLEUM" && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>WEEKLY PETROLEUM INVENTORY</span>
            <DataStatus status={statusMap.petroleum || "offline"} source="Wednesday 10:30 ET release" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10, lineHeight: 1.65 }}>
              <span style={{ color: C.green, fontWeight: 700 }}>SIGNAL MECHANIC:</span>{" "}
              Weekly crude oil inventory is released every Wednesday at 10:30 AM ET.
              One of the most-traded data releases in commodities.
              Draw = bullish (demand strong or supply tight). Build = bearish. Consecutive direction carries more weight than a single print.
            </div>

            {statusMap.petroleum === "offline" && <UnavailableState />}
            {petroleum.length > 0 && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <div style={s.lbl}>LATEST INVENTORY</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: C.green, lineHeight: 1 }}>
                    {fmt(petroleum[petroleum.length - 1]?.value)}M <span style={{ fontSize: 14, color: C.textDim, fontWeight: 400 }}>barrels</span>
                  </div>
                  {petroleum.length >= 2 && (() => {
                    const draw = petroleum[petroleum.length - 2].value - petroleum[petroleum.length - 1].value;
                    return (
                      <div style={{ fontSize: 13, fontWeight: 700, color: draw > 0 ? C.green : C.red, marginTop: 3 }}>
                        {draw > 0 ? "▼" : "▲"} Week-on-week: {fmt(Math.abs(draw), 1)}M bbl {draw > 0 ? "DRAW" : "BUILD"}
                      </div>
                    );
                  })()}
                </div>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={petroleum} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: C.textDim }}
                      tickLine={false}
                      interval={Math.floor(petroleum.length / 5)}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: C.textDim }}
                      tickLine={false}
                      axisLine={false}
                      domain={["auto", "auto"]}
                      tickFormatter={(v) => `${v}M`}
                      width={44}
                    />
                    <Tooltip
                      contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: font }}
                      formatter={(v) => [`${fmt(v)}M bbl`, "Inventory"]}
                    />
                    <Bar dataKey="value" fill={C.green} opacity={0.7} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
                  <div style={{ marginTop: 10 }}>
                    <AnalystButton
                      panel="petroleum"
                      label="SYNTHESIZE"
                      context={
                        `EIA weekly crude oil inventory (millions of barrels), last ${petroleum.length} weeks:\n` +
                        petroleum.slice(-8).map((d) => `${d.date}: ${fmt(d.value)}M`).join(", ") +
                        `\nLatest: ${fmt(petroleum[petroleum.length - 1]?.value)}M` +
                        `\nPrior: ${fmt(petroleum[petroleum.length - 2]?.value)}M` +
                        `\nWeek change: ${fmt((petroleum[petroleum.length - 1]?.value || 0) - (petroleum[petroleum.length - 2]?.value || 0), 1)}M\n\n` +
                        `Analyze the inventory trend, what the build/draw pattern implies for crude prices, ` +
                        `and how this compares to seasonal norms for this time of year.`
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── CONGRESS ── */}

      {/* ── THERMAL ── */}
      {active === "THERMAL" && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>THERMAL ANOMALIES</span>
            <DataStatus status={statusMap.thermal || "offline"} source="" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10, lineHeight: 1.65 }}>
              <span style={{ color: C.orange, fontWeight: 700 }}>SIGNAL MECHANIC:</span>{" "}
              Satellite sensors detect heat signatures globally. Industrial flaring changes
              signal production shifts. Agricultural burns signal crop supply. Refinery fires
              signal supply disruption. Conflict signatures signal infrastructure damage.
              FRP (Fire Radiative Power in MW) = intensity.
            </div>
            {repUnavailable(statusMap.thermal, fires) ? <UnavailableState /> : fires.map((f, i) => {
              const col = typeColors[f.type] || C.textDim;
              return (
                <div key={i} style={{ ...s.card(), borderLeft: `3px solid ${col}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span style={s.tag(col)}>{(f.type || "THERMAL").toUpperCase()}</span>
                        <span style={{ fontSize: 11, color: C.textDim }}>{f.date}</span>
                        <span style={{ fontSize: 11, color: C.textDim }}>
                          {f.lat?.toFixed ? `${f.lat.toFixed(1)}°N` : f.lat}, {f.lon?.toFixed ? `${f.lon.toFixed(1)}°` : f.lon}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: C.text }}>{f.label}</div>
                    </div>
                    <div style={{ textAlign: "right", fontSize: 11, flexShrink: 0 }}>
                      <div style={{ color: C.amber, fontWeight: 700 }}>FRP: {fmt(f.frp)}MW</div>
                      <div style={{ color: C.textDim }}>{fmt(f.brightness, 0)}K bright</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && !repUnavailable(statusMap.thermal, fires) && (
              <AnalystButton
                panel="signal_board"
                label="SYNTHESIZE"
                context={
                  `NASA FIRMS thermal anomalies:\n` +
                  fires.map((f) =>
                    `${f.type} at ${f.lat?.toFixed ? f.lat.toFixed(1) : f.lat}°N/${f.lon?.toFixed ? f.lon.toFixed(1) : f.lon}° — ` +
                    `${f.label} (FRP: ${fmt(f.frp)}MW, brightness: ${fmt(f.brightness, 0)}K, date: ${f.date})`
                  ).join("\n") +
                  `\n\nAnalyze which thermal events have investable signal implications — ` +
                  `industrial/refinery events for energy supply, agricultural burns for commodity supply, ` +
                  `conflict signatures for geopolitical risk premiums.`
                }
              />
            )}
          </div>
        </div>
      )}

      {/* ── UNUSUAL OPTIONS ── */}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// UNUSUAL OPTIONS TAB  (top-level; moved out of AltDataTab's sub-navigation)
// ═══════════════════════════════════════════════════════════════════════════════

// When the desk saw a UO signal, as ET HH:MM.
// ⚠⚠ PREFERS detected_at OVER timestamp, AND THEY ARE NOT THE SAME THING.
// `timestamp` is the EXCHANGE's trade time, carried straight off the Alpaca
// trade; `detected_at` is when the classifier actually fired. Live they agree to
// within milliseconds, but they diverge for anything replayed, backfilled, or
// arriving behind a stream reconnect — and "when did we see this" is the question
// a desk is asking. Falls back to `timestamp` for rows stored before detected_at
// shipped, and for batch-scanner rows, which have no detection concept at all.
// ⚠ Reuses dpEtClock rather than adding a second formatter — same input (UTC ISO
// string), same output (ET clock). It is declared lower in this file, which is
// safe because this runs at RENDER time, long after module evaluation completes.
const uoWhen = (r) => dpEtClock(r?.detected_at || r?.timestamp);

export function UnusualOptionsTab({ keys, onNavigate }) {
  const [unusualOpts, setUnusualOpts] = useState([]);   // /api/unusual-options/scan results
  const [unusualAt,   setUnusualAt]   = useState(null);  // ms of last update
  const [uoSide,      setUoSide]      = useState("ALL"); // ALL | CALLS | PUTS
  const [uoStrength,  setUoStrength]  = useState("ALL"); // ALL | EXTREME | STRONG | UNUSUAL
  const [uoDetail,    setUoDetail]    = useState(null);  // expanded row key (inline detail)
  const [uoStatus,    setUoStatus]    = useState("offline");
  const [uoSource,    setUoSource]    = useState(null);  // "stream" | "batch" | null
  const [uoQuery,     setUoQuery]     = useState("");    // ticker-prefix search box

  // ⚠ SERVER-SIDE SEARCH. The live list is the market-wide top 100 by premium out
  // of ~16,000 signals, so filtering it in the browser would miss most tickers.
  // ?q= asks the backend to filter by ticker prefix before its top-100 cut.
  const fetchUoSearch = useCallback(async (q) => {
    const res = await backendFetch("https://data.alpaca.markets",
      `/api/stream/unusual/recent?q=${encodeURIComponent(q)}`);
    if (!res.ok) throw new Error(String(res.status));
    return res.json();
  }, []);
  const uoSearch = useRemoteSearch(uoQuery, fetchUoSearch);
  const uoSearchActive = useRef(false);
  uoSearchActive.current = uoSearch.active;
  const uoSearchRefresh = useRef(uoSearch.refresh);
  uoSearchRefresh.current = uoSearch.refresh;

  // ⚠ STREAM FIRST, BATCH AS FALLBACK — and the label follows whichever won, so
  // the header can no longer claim "LIVE STREAM" while showing batch rows (it did
  // exactly that for weeks: the panel was hardcoded to say LIVE STREAM while its
  // only fetch was /api/unusual-options/scan, the batch scanner).
  // ⚠ /api/unusual-options/scan is now CACHE-ONLY and returns an OBJECT
  // {results, cached, message}, not the bare array it used to. It no longer
  // triggers a live Trading-API fan-out on mount (that cost ~17s per cold load).
  const fetchUnusualOptions = useCallback(async () => {
    setUoStatus("loading");
    let rows = [];
    let src = null;
    let reached = false;
    try {
      const res = await backendFetch("https://data.alpaca.markets", "/api/stream/unusual/recent");
      const d = await res.json();
      reached = true;
      const r = Array.isArray(d?.results) ? d.results : [];
      if (r.length) { rows = r; src = "stream"; }
    } catch { /* fall through to the batch cache */ }
    if (!rows.length) {
      try {
        const res = await backendFetch("https://data.alpaca.markets", "/api/unusual-options/scan");
        const d = await res.json();
        reached = true;
        // Tolerate the legacy bare-array shape as well as the new object.
        const r = Array.isArray(d?.results) ? d.results : (Array.isArray(d) ? d : []);
        if (r.length) { rows = r; src = "batch"; }
      } catch { /* both sources unreachable */ }
    }
    setUnusualOpts(rows);
    setUoSource(src);
    setUnusualAt(Date.now());
    setUoStatus(rows.length ? "live" : (reached ? "delayed" : "offline"));
  }, []);

  // Fetch on mount, then every 5 min during market hours (the scan runs 9:30–4 ET).
  // ⚠ While a search is active the poll refreshes the SEARCH, not the live list,
  // so a tick never swaps the user's results for the market-wide top 100.
  useEffect(() => {
    fetchUnusualOptions();
    const id = setInterval(() => {
      const et = dpEtNow();
      const open = et.weekday !== "Sat" && et.weekday !== "Sun"
        && et.minutes >= DP_OPEN_MIN && et.minutes < DP_CLOSE_MIN;
      if (!open) return;
      if (uoSearchActive.current) uoSearchRefresh.current();
      else fetchUnusualOptions();
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchUnusualOptions]);

  // Clearing the box brings the live view back fresh, not as it was before the search.
  const uoWasSearching = useRef(false);
  useEffect(() => {
    if (uoWasSearching.current && !uoSearch.active) fetchUnusualOptions();
    uoWasSearching.current = uoSearch.active;
  }, [uoSearch.active, fetchUnusualOptions]);

  // ⚠ The expanded-row key includes the row's POSITION, and a search reorders
  // everything. Collapse on every new query and every new search response, so a
  // key can never land on a different contract.
  useEffect(() => { setUoDetail(null); }, [uoSearch.q, uoSearch.data]);

  const et = dpEtNow();
  const marketOpen = et.weekday !== "Sat" && et.weekday !== "Sun"
    && et.minutes >= DP_OPEN_MIN && et.minutes < DP_CLOSE_MIN;
  const uoSearchDone = uoSearch.active && uoSearch.status === "done";
  const baseRows = uoSearch.active ? (uoSearchDone ? (uoSearch.data?.results || []) : []) : unusualOpts;
  const rows = baseRows.filter((r) => {
    if (uoSide === "CALLS" && r.option_type !== "call") return false;
    if (uoSide === "PUTS" && r.option_type !== "put") return false;
    if (uoStrength !== "ALL" && !String(r.signal).startsWith(uoStrength)) return false;
    return true;
  });
  const minsAgo = unusualAt != null ? Math.floor((Date.now() - unusualAt) / 60000) : null;
  const stamp = unusualAt != null
    ? `${new Date(unusualAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}${minsAgo != null ? ` · ${minsAgo}m ago` : ""}`
    : "—";
  const loading = uoStatus === "loading";
  // ⚠ Honest source label — no signal count. The obvious count to show would be
  // stream:uo:market's totals, but that counter is broken (it reads 9 against
  // ~13,900 signals actually captured today), so displaying any count here would
  // be worse than displaying none. Restore a count only once that write path is
  // fixed in the stream service.
  const uoLabel = uoSource === "stream" ? "LIVE STREAM · Full Market"
    : uoSource === "batch" ? "CACHED SCAN"
    : "NO DATA";
  const pill = (label, val, cur, set, grp) => (
    <button key={grp + val} onClick={() => set(val)}
      style={{ ...s.btn(cur === val ? "amber" : "default"), padding: "3px 9px", fontSize: 10 }}>
      {label}
    </button>
  );
  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={s.panelT}>UNUSUAL OPTIONS FLOW</span>
        <DataStatus status={uoStatus || "offline"} source={uoLabel} />
      </div>
      <div style={s.panelB}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: C.textDim }}>{uoLabel}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 9, color: C.textDim }}>Last update: {stamp}</span>
            <button style={{ ...s.btn("teal"), padding: "3px 9px", fontSize: 10 }}
              onClick={() => fetchUnusualOptions()} disabled={loading}>
              <RefreshCw size={10} style={{ verticalAlign: "middle", marginRight: 3 }} />
              {loading ? "..." : "REFRESH"}
            </button>
          </span>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10, alignItems: "center" }}>
          {pill("ALL", "ALL", uoSide, setUoSide, "side")}
          {pill("CALLS", "CALLS", uoSide, setUoSide, "side")}
          {pill("PUTS", "PUTS", uoSide, setUoSide, "side")}
          <span style={{ width: 1, alignSelf: "stretch", background: C.border, margin: "0 4px" }} />
          {pill("ALL", "ALL", uoStrength, setUoStrength, "str")}
          {pill("EXTREME", "EXTREME", uoStrength, setUoStrength, "str")}
          {pill("STRONG", "STRONG", uoStrength, setUoStrength, "str")}
          {pill("UNUSUAL", "UNUSUAL", uoStrength, setUoStrength, "str")}
        </div>

        <SearchBox value={uoQuery} onChange={setUoQuery} placeholder="Search by ticker..." />
        {uoSearch.active && (
          <div style={{ fontSize: 10, color: C.textDim, marginBottom: 8, lineHeight: 1.6 }}>
            {uoSearch.status === "hint" ? "Tickers use letters, digits, “.” and “-” (up to 10 characters)."
              : uoSearch.status === "pending" || uoSearch.status === "loading" ? "Searching…"
              : uoSearch.status === "error" ? "Search unavailable. Try again in a moment."
              : <>
                  <span style={{ color: C.text }}>{rows.length}</span> {rows.length === 1 ? "match" : "matches"}
                  {rows.length !== baseRows.length ? ` (of ${baseRows.length} before filters)` : ""}
                  {` for tickers starting “${uoSearch.q.toUpperCase()}”`}
                  {(uoSearch.data?.total_signals || 0) > (uoSearch.data?.results || []).length
                    ? ` · top ${(uoSearch.data.results || []).length} of ${uoSearch.data.total_signals} signals by premium`
                    : ""}
                  {uoSearch.data?.truncated
                    ? ` · first ${uoSearch.data.lists_searched} of ${uoSearch.data.lists_matched} matching tickers searched — type more to narrow`
                    : ""}
                </>}
          </div>
        )}

        {uoSearch.active && !uoSearchDone ? null
        : uoSearch.active && !baseRows.length ? (
          <div style={{ fontSize: 10, color: C.textDim, textAlign: "center", padding: 16 }}>
            No unusual options for tickers starting “{uoSearch.q.toUpperCase()}”.
          </div>
        ) : !uoSearch.active && loading && !unusualOpts.length ? (
          <div style={{ fontSize: 10, color: C.textDim, textAlign: "center", padding: 16 }}>
            {"Scanning S&P 500 for unusual options flow..."}
          </div>
        ) : !uoSearch.active && !unusualOpts.length ? (
          <div style={{ fontSize: 10, color: C.textDim, textAlign: "center", padding: 16, lineHeight: 1.6 }}>
            {/* ⚠ The old after-hours copy claimed the scan "runs during market hours",
                which is no longer true of the primary source: stream:uo:{"{sym}"} keys
                carry a 24h TTL, so today's captured flow stays readable overnight.
                An empty panel after hours now means nothing fired, not that the
                feature is asleep. */}
            {marketOpen
              ? "No unusual options activity detected above threshold."
              : <>{"No unusual options flow captured in today's session."}<br />Last checked: {stamp}</>}
          </div>
        ) : !rows.length ? (
          <div style={{ fontSize: 10, color: C.textDim, textAlign: "center", padding: 16 }}>
            {uoSearch.active
              ? `No matches for “${uoSearch.q.toUpperCase()}” with the current filters.`
              : "No results match the current filters."}
          </div>
        ) : (
          <div>
            {rows.map((r, i) => {
              const isCall = r.option_type === "call";
              const dir = isCall ? C.green : C.red;
              const urg = UO_URGENCY[r.urgency] || { c: C.textDim, l: r.urgency };
              const key = `${r.ticker}-${r.expiry}-${r.strike}-${r.option_type}-${i}`;
              const open = uoDetail === key;
              // ⚠ Hide-when-null. Each fragment renders only when present, so a
              // missing strike or expiry leaves no "$" or dangling "·", and the
              // clocks (which return "—" when they cannot parse) are dropped too.
              const contract = [uoReal(r.strike) ? `$${r.strike}` : null,
                                uoText(r.option_type) ? (isCall ? "C" : "P") : null].filter(Boolean).join(" ");
              const contractLine = [contract, uoText(r.expiry) ? r.expiry : null].filter(Boolean).join(" · ");
              const when = uoWhen(r);
              const tradeAt = uoText(r.timestamp) ? dpEtClock(r.timestamp, true) : "—";
              const detectedAt = uoText(r.detected_at) ? dpEtClock(r.detected_at, true) : "—";
              // A real 0 still displays ("0", "0%", "0d"); a negative DTE (a 0DTE
              // row still listed after its expiry) is not a reading and is hidden.
              const detail = [
                uoReal(r.volume) && ["Vol", r.volume.toLocaleString()],
                uoReal(r.open_interest) && ["OI", r.open_interest.toLocaleString()],
                uoReal(r.implied_vol) && ["IV", `${(r.implied_vol * 100).toFixed(0)}%`],
                uoReal(r.otm_pct) && ["OTM", `${r.otm_pct}%`],
                uoReal(r.days_to_expiry) && r.days_to_expiry >= 0 && ["DTE", `${r.days_to_expiry}d`],
                // ⚠ Both times, labelled apart, and only when they exist.
                // Collapsing them into one line would hide exactly the case
                // worth seeing: a Detected materially later than Trade means
                // the print reached us behind a reconnect or a replay.
                tradeAt !== "—" && ["Trade", tradeAt],
                detectedAt !== "—" && ["Detected", detectedAt],
              ].filter(Boolean);
              return (
                <div key={key} onClick={() => setUoDetail(open ? null : key)}
                  style={{ borderBottom: `1px solid ${C.border}`, padding: "7px 4px", cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.panelAlt)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: dir, minWidth: 52 }}>{r.ticker}</span>
                    {contractLine && <span style={{ fontSize: 11, color: C.text }}>{contractLine}</span>}
                    {/* ⚠ Stream rows carry no vol/OI ratio — open interest is not on
                        the market-data API, only the Trading API contract endpoint,
                        and uo_classifier was deliberately rewritten to make zero REST
                        calls. Omit the chip rather than render "nullx". */}
                    {r.volume_oi_ratio != null && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: uoRatioColor(r.volume_oi_ratio) }}>{r.volume_oi_ratio}x</span>
                    )}
                    {uoReal(r.total_premium) && <span style={{ fontSize: 11, color: C.text }}>{fmtUsd(r.total_premium)}</span>}
                    {uoText(r.urgency) && <span style={{ fontSize: 9, fontWeight: 700, color: urg.c, border: `1px solid ${urg.c}66`, borderRadius: 2, padding: "0 4px", whiteSpace: "nowrap" }}>{urg.l}</span>}
                    {uoText(r.signal) && <span style={uoSignalStyle(r.signal)}>{String(r.signal).replace("_", " ")}</span>}
                    {/* ⚠ Right-aligned via marginLeft:auto on the TIME rather than a
                        column header: this row is a wrapping flex list, not a table,
                        so a "TIME" heading would label a column that does not exist
                        once the fields wrap. The chevron follows it on the gap. */}
                    {/* ⚠ Carries the marginLeft:auto so it starts the right-hand
                        group. The timestamp keeps its own auto margin, so flexbox
                        splits the free space between the two and the timestamp
                        still ends at the right edge — nothing that was already on
                        the row shifts position. */}
                    <span style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                      <ChartJumpButton symbol={r.ticker} onNavigate={onNavigate} />
                    </span>
                    {when !== "—" && (
                      <span style={{ marginLeft: "auto", fontSize: 9, color: C.textDim, whiteSpace: "nowrap" }}>
                        {when}
                      </span>
                    )}
                    <span style={{ fontSize: 9, color: C.textDim }}>{open ? "▲" : "▼"}</span>
                  </div>
                  {uoText(r.note) && <div style={{ fontSize: 10, color: C.textDim, marginTop: 3, lineHeight: 1.4 }}>{r.note}</div>}
                  {/* The whole line is omitted when no item is left — no empty strip. */}
                  {open && detail.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 6, fontSize: 10, color: C.textDim }}>
                      {detail.map(([label, value]) => (
                        <span key={label}>{label}: <span style={{ color: C.text }}>{value}</span></span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {rows.length > 0 && (keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
          <div style={{ marginTop: 10 }}>
            <AnalystButton
              panel="unusual_options"
              label="SYNTHESIZE"
              context={
                `Unusual options flow — ${uoSource === "stream" ? "live stream, full market" : "cached platform scan"}, top by premium:\n` +
                rows.slice(0, 10).map((r) =>
                  `${r.ticker} $${r.strike} ${r.option_type} exp ${r.expiry}: ` +
                  // vol/OI and OI only exist on the batch path — omit rather than send "null" to the model.
                  (r.volume_oi_ratio != null ? `${r.volume_oi_ratio}x vol/OI ` : "") +
                  (r.volume != null ? `(${Number(r.volume).toLocaleString()} vol` : "(") +
                  (r.open_interest != null ? ` / ${Number(r.open_interest).toLocaleString()} OI), ` : "), ") +
                  `${fmtUsd(r.total_premium)} premium, ${r.signal}, ${r.urgency}`
                ).join("\n") +
                `\n\nWhat does this unusual options flow suggest about market positioning right now?`
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONGRESSIONAL TAB  (top-level; moved out of AltDataTab's sub-navigation)
// ═══════════════════════════════════════════════════════════════════════════════

export function CongressionalTab({ keys, onData, onFollowMoney }) {
  const [congress, setCongress] = useState([]);
  // ⚠ null means "age unknown" and renders nothing — never a fabricated 0h.
  const [freshness, setFreshness] = useState(null);
  // ⚠ Starts "loading", not "offline": "offline" now means a failed fetch and
  // would flash the unavailable banner before the first request settles.
  const [status,   setStatus]   = useState("loading");

  const fetchCongress = useCallback(async (forceRefresh = false) => {
    setStatus("loading");
    try {
      const res = await backendFetch(
        "https://api.quiverquant.com",
        "/api/altdata/congress" + forceParam(forceRefresh)
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      // ⚠ A placeholder payload yields no rows, so it takes the failure path
      // below. That also keeps data_freshness off it: a freshness line can never
      // appear over stand-in rows even if the backend envelope changes.
      const list = liveRows(data);
      if (list.length) {
        setCongress(list);
        if (onData) onData("congressTrades", list);
        setStatus("live");
        setFreshness(data.data_freshness || null);
      } else throw new Error("empty");
    } catch {
      setCongress([]);
      setStatus("offline");
      setFreshness(null);
    }
  }, [onData]);

  useEffect(() => { fetchCongress(); }, [fetchCongress]);

  return (
    <CongressPanel
      congress={congress}
      keys={keys}
      status={status}
      freshness={freshness}
      onRetry={() => fetchCongress(true)}
      onFollowMoney={onFollowMoney}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DEEP SIGNALS TAB
// ═══════════════════════════════════════════════════════════════════════════════

const DEEP_PANELS = [
  { id: "CONTRACTS", label: "GOV CONTRACTS",  color: C.amber  },
  { id: "FDA",       label: "BIOTECH",        color: "#ec4899"},
  { id: "PATENTS",   label: "PATENT INTEL",   color: C.purple },
  { id: "COURTS",    label: "COURT WATCH",    color: C.red    },
  { id: "BANKS",     label: "BANK HEALTH",    color: C.teal   },
];

// ⚠⚠ REP_FDA IS GONE ON PURPOSE. It was five invented approvals (KEYTRUDA,
// OZEMPIC, a row literally reading "PIPELINE TBD / PENDING") shown whenever the
// upstream failed, badged "mock" in a corner. Biotech data is either real or
// absent — every section below has an honest empty state instead.

// Status pill colours for a trial's recruitment state.
const BIO_STATUS_STYLE = {
  RECRUITING:            { bg: C.greenBg, fg: C.green,   label: "RECRUITING" },
  ACTIVE_NOT_RECRUITING: { bg: "#0b1220", fg: C.blue,    label: "ACTIVE" },
  NOT_YET_RECRUITING:    { bg: C.panelBg, fg: C.textDim, label: "NOT YET RECRUITING" },
  ENROLLING_BY_INVITATION: { bg: C.panelBg, fg: C.textDim, label: "BY INVITATION" },
};

function BioStatusPill({ status }) {
  const st = BIO_STATUS_STYLE[status] || { bg: C.panelBg, fg: C.textDim, label: status || "—" };
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 2,
      background: st.bg, color: st.fg, whiteSpace: "nowrap",
    }}>
      {st.label}
    </span>
  );
}

// ⚠ Amber inside 30 days. A readout a month out is the one worth acting on;
// past that the badge stays dim so the urgent ones actually stand out.
function BioDaysBadge({ days }) {
  const near = typeof days === "number" && days <= 30;
  return (
    <span style={{ fontSize: 13, fontWeight: 700, color: near ? C.amber : C.textDim }}>
      {typeof days === "number" ? `${days} day${days === 1 ? "" : "s"}` : "—"}
    </span>
  );
}

function BioSectionHead({ title, sub }) {
  return (
    <>
      <div style={{ fontSize: 11, color: "#ec4899", fontWeight: 700,
                    letterSpacing: "0.1em", margin: "16px 0 2px" }}>
        {title}
      </div>
      <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8 }}>{sub}</div>
    </>
  );
}

// Per-section empty state. Never a fabricated row.
function BioEmpty({ text }) {
  return (
    <div style={{ fontSize: 12, color: C.textDim, padding: "10px 0", lineHeight: 1.6 }}>
      {text}
    </div>
  );
}

// ⚠⚠ REP_PATENTS IS GONE. It rendered five invented filings — "APPLE INC",
// "MODERNA INC", fabricated abstracts — as real data whenever the upstream
// failed, and the upstream ALWAYS failed: search.patentsview.org does not
// resolve from Railway. The panel now shows an honest empty state instead.
// Same removal as REP_BANKS, for the same reason.

// ⚠⚠ REP_BANKS IS GONE. It carried a hardcoded "SVB — HISTORICAL" row with
// invented npl/tier1 figures, shown whenever the upstream failed — which was
// EVERY time, because the FDIC host had moved and the backend 502'd on every
// call. It also had assets one thousand times too small for fmtB, so JPMorgan
// rendered as "$3.2B". Bank data is either real or absent now.

// ⚠ Every threshold below is applied to a REAL FDIC figure for the first time.
// npl_ratio and tier1 previously arrived undefined on live data, so the badge
// fell through to STABLE for every bank regardless of condition.
function bankNplColor(v) {
  if (v == null) return C.textDim;
  return v < 1 ? C.green : v <= 3 ? C.amber : C.red;
}
function bankTier1Color(v) {
  if (v == null) return C.textDim;
  return v > 12 ? C.green : v >= 8 ? C.amber : C.red;
}
function bankLtdColor(v) {
  if (v == null) return C.textDim;
  return v < 80 ? C.green : v <= 90 ? C.amber : C.red;
}
// null renders "—", never 0.00%. Measured on the live top 50, two banks have a
// null tier1 and two a null net income; printing those as zero would read as
// distress rather than as an absent filing.
function bankPct(v) {
  return v == null ? "—" : `${Number(v).toFixed(2)}%`;
}

// STRESSED / WATCH / STABLE from the real ratios.
function bankStatus(b) {
  const npl = b.npl_ratio, t1 = b.tier1;
  if ((npl != null && npl > 3) || (t1 != null && t1 < 8)) return "STRESSED";
  if ((npl != null && npl > 1) || (t1 != null && t1 < 10)) return "WATCH";
  return "STABLE";
}
const BANK_STATUS_COLOR = { STRESSED: C.red, WATCH: C.amber, STABLE: C.green };
const BANK_STATUS_RANK = { STRESSED: 0, WATCH: 1, STABLE: 2 };

// ─── DARK POOL INTELLIGENCE ───────────────────────────────────────────────────
// Off-exchange TRF prints (Alpaca full tape). Terminal-grade: no gamification,
// no bullish/bearish labels — factual institutional order-flow assessment.
const dpNotional = (v) => {
  const n = Number(v) || 0;
  const a = Math.abs(n), sign = n < 0 ? "-" : "";
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3)}K`;
  return `${sign}$${Math.round(a)}`;
};
const DP_TICK = { UP: "↑", DOWN: "↓", NEUTRAL: "→" };
const dpTickColor = (t) => (t === "UP" ? C.green : t === "DOWN" ? C.red : C.textDim);

const dpClusterCenter = (cluster) => {
  const m = /\$([\d.]+)-\$([\d.]+)/.exec(cluster?.price_range || "");
  return m ? (parseFloat(m[1]) + parseFloat(m[2])) / 2 : null;
};
function dpClusterAssessment(cluster) {
  const c = dpClusterCenter(cluster);
  const center = c != null ? c.toFixed(2) : "—";
  const n = cluster.total_notional || 0;
  if (cluster.dominant_tick === "UP" && n > 50e6) return `Institutional accumulation bias at $${center}`;
  if (cluster.dominant_tick === "DOWN" && n > 50e6) return `Institutional distribution bias at $${center}`;
  if (cluster.dominant_tick === "MIXED") return `Two-sided institutional activity at $${center} — decision point`;
  return `Clustered institutional activity at $${center}`;
}

function dpBuildContext(data) {
  if (!data) return "No dark pool data.";
  const vp = data.volume_profile || {}, nf = data.net_flow || {};
  const levels = (data.defended_levels || []).slice(0, 3);
  const c = data.cluster;
  return (
    `Dark pool (off-exchange TRF) prints for ${data.symbol} over the last ${data.window_minutes} min:\n` +
    `Dark pool share: ${Math.round((vp.dark_pct || 0) * 100)}% ` +
    `(dark ${Number(vp.dark_volume || 0).toLocaleString()} sh / day volume ${Number(vp.lit_volume || 0).toLocaleString()} sh).\n` +
    `Net flow: ${nf.bias} (uptick ${dpNotional(nf.uptick_notional)}, downtick ${dpNotional(nf.downtick_notional)}, net ${dpNotional(nf.net)}).\n` +
    `Top defended levels:\n` +
    (levels.length ? levels.map((l) => `  $${l.price}: ${dpNotional(l.notional)}, ${l.print_count} prints (${l.strength})`).join("\n") : "  (none)") +
    `\nCluster: ${c?.detected ? `${c.price_range}, ${c.print_count} prints, ${dpNotional(c.total_notional)}, dominant ${c.dominant_tick}` : "none detected"}\n` +
    `Block prints shown: ${(data.block_prints || []).length}.\n\n` +
    `Assess institutional intent from the defended levels, net tick direction (buyer vs seller ` +
    `initiated), block size distribution, and whether the cluster indicates accumulation or distribution.`
  );
}

// ASCII timeline: 18 five-minute segments, dot sized by the largest print in each.
function DarkPoolTimeline({ prints, window }) {
  const segments = 18;
  const end = Date.now();
  const startMs = end - window * 60000;
  const span = end - startMs || 1;
  const maxN = new Array(segments).fill(0);
  for (const p of prints || []) {
    const ts = new Date(p.timestamp).getTime();
    if (Number.isNaN(ts) || ts < startMs) continue;
    let idx = Math.floor(((ts - startMs) / span) * segments);
    idx = Math.max(0, Math.min(segments - 1, idx));
    if (p.notional > maxN[idx]) maxN[idx] = p.notional;
  }
  const line = maxN.map((n) => (n <= 0 ? "─" : n < 5e6 ? "·" : n < 20e6 ? "●" : "◉")).join("");
  const clock = (ms) => new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 10, color: C.textDim, letterSpacing: "0.2em", fontFamily: font, whiteSpace: "nowrap", overflowX: "auto" }}>{line}</div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: C.textDim, marginTop: 2 }}>
        <span>{clock(startMs)}</span>
        <span>{clock(end)}</span>
      </div>
    </div>
  );
}

export function DarkPoolPanel({ keys, onData, initialSymbol = "SPY" }) {
  const [symbol,  setSymbol]  = useState(initialSymbol);
  const [input,   setInput]   = useState(initialSymbol);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [updated, setUpdated] = useState("");

  const fetchDarkpool = useCallback(async (sym) => {
    if (!sym) return;
    setLoading(true);
    try {
      const res = await backendFetch("https://data.alpaca.markets", `/api/darkpool/${sym}`);
      const d = await res.json();
      setData(d);
      setUpdated(new Date().toLocaleTimeString());
      // Signal Board: push a defended cluster > $50M to activate the rule.
      if (onData && d?.cluster?.detected && d.cluster.total_notional > 50e6) {
        onData("darkPoolDefended", {
          symbol: d.symbol,
          level: dpClusterCenter(d.cluster),
          notional: d.cluster.total_notional,
          bias: d.cluster.dominant_tick,
          strength: d.cluster.print_count,
        });
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [onData]);

  // Fetch on symbol change + auto-refresh every 2 min while the panel is mounted.
  useEffect(() => {
    fetchDarkpool(symbol);
    const id = setInterval(() => fetchDarkpool(symbol), 120000);
    return () => clearInterval(id);
  }, [symbol, fetchDarkpool]);

  const submit = () => { const s = input.trim().toUpperCase(); if (s) setSymbol(s); };

  const vp = data?.volume_profile;
  const nf = data?.net_flow;
  const levels = data?.defended_levels || [];
  const blocks = data?.block_prints || [];
  const cluster = data?.cluster;
  const darkPct = vp ? Math.round((vp.dark_pct || 0) * 100) : 0;
  const maxLevel = levels.length ? Math.max(...levels.map((l) => l.notional)) : 0;
  const netColor = nf?.bias === "BUYER" ? C.green : nf?.bias === "SELLER" ? C.red : C.amber;
  const hasData = data && (blocks.length || levels.length || (vp && vp.dark_volume));

  const sectionLabel = (t) => (
    <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.12em", marginTop: 12, marginBottom: 5 }}>{t}</div>
  );
  const bar = (pct, color, w = 160) => (
    <span style={{ display: "inline-block", width: w, height: 6, background: C.border, borderRadius: 2, overflow: "hidden", verticalAlign: "middle" }}>
      <span style={{ display: "block", width: `${Math.max(0, Math.min(100, pct))}%`, height: "100%", background: color }} />
    </span>
  );

  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={s.panelT}>DARK POOL INTELLIGENCE</span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="SYMBOL"
            style={{ width: 78, background: C.panel, border: `1px solid ${C.border}`, color: C.text, fontFamily: font, fontSize: 12, padding: "4px 8px", borderRadius: 3, textTransform: "uppercase" }}
          />
          <button style={{ ...s.btn("amber"), padding: "4px 10px" }} onClick={submit}>LOAD</button>
          <button style={{ ...s.btn("teal"), padding: "4px 10px" }} onClick={() => fetchDarkpool(symbol)} disabled={loading} title="Refresh">
            <RefreshCw size={11} style={{ verticalAlign: "middle" }} />
          </button>
        </div>
      </div>
      <div style={s.panelB}>
        <div style={{ fontSize: 11, color: C.amber, fontWeight: 700, letterSpacing: "0.15em" }}>
          DARK POOL INTELLIGENCE — {data?.symbol || symbol}
        </div>
        <div style={{ fontSize: 9, color: C.textDim, marginBottom: 4 }}>
          Last {data?.window_minutes || 90} min · Updated {updated || "—"}
        </div>

        {loading && !data ? (
          <div style={{ fontSize: 10, color: C.textDim, marginTop: 8 }}>Loading dark pool prints...</div>
        ) : !hasData ? (
          <div style={{ fontSize: 10, color: C.textDim, marginTop: 8, lineHeight: 1.6 }}>
            No off-exchange prints in the window. A full-tape market-data entitlement is required to surface dark pool prints.
          </div>
        ) : (
          <>
            {sectionLabel("VOLUME PROFILE")}
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginBottom: 3 }}>
              <span style={{ color: C.textDim, width: 72 }}>Dark Pool:</span>
              {bar(darkPct, C.green)}
              <span style={{ color: C.text }}>{darkPct}%</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
              <span style={{ color: C.textDim, width: 72 }}>Lit Market:</span>
              {bar(100 - darkPct, C.textDim)}
              <span style={{ color: C.text }}>{100 - darkPct}%</span>
            </div>

            {sectionLabel("NET FLOW")}
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginBottom: 3 }}>
              <span style={{ color: C.textDim, width: 104 }}>Uptick prints:</span>
              <span style={{ color: C.text, width: 66 }}>{dpNotional(nf?.uptick_notional)}</span>
              <span style={{ color: C.green }}>↑</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginBottom: 3 }}>
              <span style={{ color: C.textDim, width: 104 }}>Downtick prints:</span>
              <span style={{ color: C.text, width: 66 }}>{dpNotional(nf?.downtick_notional)}</span>
              <span style={{ color: C.red }}>↓</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
              <span style={{ color: C.textDim, width: 104 }}>Net:</span>
              <span style={{ color: netColor, fontWeight: 700, width: 66 }}>{dpNotional(nf?.net)}</span>
              <span style={{ color: netColor, fontSize: 10, fontWeight: 700 }}>
                {nf?.bias === "NEUTRAL" ? "NEUTRAL" : `${nf?.bias} BIAS`}
              </span>
            </div>

            {levels.length > 0 && (
              <>
                {sectionLabel("DEFENDED LEVELS")}
                {levels.map((lv) => (
                  <div key={lv.price} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginBottom: 3, flexWrap: "wrap" }}>
                    <span style={{ color: C.text, width: 64 }}>${fmt(lv.price)}</span>
                    {bar(maxLevel ? (lv.notional / maxLevel) * 100 : 0, C.amber, 120)}
                    <span style={{ color: C.amber, fontSize: 10 }}>{dpNotional(lv.notional)}</span>
                    <span style={{ color: C.textDim, fontSize: 10 }}>· {lv.print_count} prints</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: lv.strength === "STRONG" ? C.amber : C.textDim, opacity: lv.strength === "LIGHT" ? 0.6 : 1 }}>
                      · {lv.strength}
                    </span>
                  </div>
                ))}
              </>
            )}

            {blocks.length > 0 && (
              <>
                {sectionLabel(`BLOCK PRINTS (>${dpNotional(1e6)} notional) — last ${data?.window_minutes || 90} min`)}
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                        {["TIME", "PRICE", "SIZE", "NOTIONAL", "TICK"].map((h) => (
                          <th key={h} style={{ textAlign: h === "TIME" ? "left" : "right", padding: "3px 6px", fontSize: 9, color: C.textDim, fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {blocks.slice(0, 15).map((b, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: "3px 6px", color: C.textDim }}>{new Date(b.timestamp).toLocaleTimeString()}</td>
                          <td style={{ padding: "3px 6px", textAlign: "right", color: C.text }}>${fmt(b.price)}</td>
                          <td style={{ padding: "3px 6px", textAlign: "right", color: C.textDim }}>{Number(b.size).toLocaleString()}</td>
                          <td style={{ padding: "3px 6px", textAlign: "right", color: C.amber }}>{dpNotional(b.notional)}</td>
                          <td style={{ padding: "3px 6px", textAlign: "right", color: dpTickColor(b.tick) }}>{DP_TICK[b.tick] || "→"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <DarkPoolTimeline prints={blocks} window={data?.window_minutes || 90} />

            {cluster?.detected && (
              <>
                <div style={{ borderTop: `1px solid ${C.border}`, margin: "10px 0" }} />
                <div style={{ fontSize: 10, color: C.amber, fontWeight: 700 }}>CLUSTER DETECTED</div>
                <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>Price range: {cluster.price_range}</div>
                <div style={{ fontSize: 10, color: C.textDim }}>{cluster.print_count} prints · {dpNotional(cluster.total_notional)} notional</div>
                <div style={{ fontSize: 10, color: C.textDim }}>Dominant direction: {cluster.dominant_tick}</div>
                <div style={{ fontSize: 10, color: C.textDim, fontStyle: "italic", marginTop: 4, lineHeight: 1.5 }}>
                  {dpClusterAssessment(cluster)}
                </div>
              </>
            )}

            {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
              <div style={{ marginTop: 10 }}>
                <AnalystButton panel="darkpool" label="SYNTHESIZE" context={dpBuildContext(data)} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DARK POOL SCANNER — market-wide anomaly scan over a dynamic universe
// ═══════════════════════════════════════════════════════════════════════════════

// Stream dark-pool classification → badge color (MIDPOINT brightest = negotiated block).
const dpClassColor = (c) =>
  c === "MIDPOINT"    ? C.text
  : c === "BUYER"     ? C.green
  : c === "SELLER"    ? C.red
  : c === "ABOVE_MID" ? "rgba(0,255,135,0.55)"
  : c === "BELOW_MID" ? "rgba(255,82,82,0.55)"
  : C.textDim;
// Signal-strength dots: 3 = ●●● MIDPOINT, 2 = ●● BUYER/SELLER, 1 = ● lean.
const dpDots = (n) => "●".repeat(Math.max(0, Math.min(3, Number(n) || 0))) || "—";

// Current time in US Eastern (no libs; Intl handles DST) → date, month/year, minutes-of-day.
const dpEtNow = () => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", weekday: "short", hour12: false,
  }).formatToParts(new Date()).map((x) => [x.type, x.value]));
  let hour = parseInt(p.hour, 10); if (hour >= 24) hour = 0;
  return {
    date: `${p.year}-${p.month}-${p.day}`,
    month: parseInt(p.month, 10), year: parseInt(p.year, 10),
    minutes: hour * 60 + parseInt(p.minute, 10), weekday: p.weekday,
  };
};
const DP_OPEN_MIN = 9 * 60 + 30, DP_CLOSE_MIN = 16 * 60;


// ── Dark-pool TAPE ────────────────────────────────────────────────────────────
// Every symbol the stream captured an institutional print for today gets exactly
// one row, summary-only, sorted most-recently-active first. Prints for a symbol
// load on expand.
//
// ⚠⚠ NO SCAN BUTTON, NO MARKET-HOURS GATE, NO UNIVERSE. All three were removed
// deliberately and none should come back:
// - The scan was a REST fan-out over a hardcoded ETF/watchlist universe. The whole
//   tape is already in Redis (dp:summary:*, ~1,175 symbols today), written by the
//   stream, so there is nothing to "run".
// - The display used to switch on market state and fall back to a session-replay
//   view, which meant the day's captured flow was unreachable outside 09:30-16:00
//   even though dp:summary carries a 24h TTL. Only the POLL CADENCE depends on
//   market state now (30s open / 5 min closed).
// - A universe list can only exclude. The tape's rule is: if the stream saw a
//   print, the symbol has a row.
const DP_TAPE_POLL_OPEN_MS = 30000;
const DP_TAPE_POLL_CLOSED_MS = 300000;

const dpBiasColor = (b) =>
  b === "BUYER" ? C.green : b === "SELLER" ? C.red : b === "MIDPOINT" ? C.teal : C.textDim;

// Stored print/summary timestamps are UTC ISO; the desk reads ET.
const dpEtClock = (iso, withSeconds) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", hour12: false,
    hour: "2-digit", minute: "2-digit", ...(withSeconds ? { second: "2-digit" } : {}),
  }).format(d);
};

// ─── CHART JUMP ───────────────────────────────────────────────────────────────
// One button, two tapes. Seeds the Charts tab's ticker and switches to it.
//
// ⚠⚠ localStorage IS THE HANDOFF, AND IT WORKS ONLY BECAUSE TABS UNMOUNT.
// ChartsTab restores its symbol in a `useEffect(..., [])` — mount only. App
// renders every tab as `{activeTab === "X" && <Tab/>}`, so leaving CHARTS
// unmounts it and returning REMOUNTS it, re-running that restore. Write then
// navigate is therefore read on arrival. If tabs are ever changed to stay mounted
// and merely hidden, this silently stops working: the write lands, the tab
// switches, and the chart keeps whatever symbol it had. There is no prop path to
// ChartsTab today — it takes only keys and globalSignals.
// ⚠ The key is "fterm_chart_symbol", verified against ChartsTab's own read. Not
// a new channel: this is the same key ChartsTab already writes on every symbol
// change to persist the selection.
//
// ⚠⚠ stopPropagation IS MANDATORY, NOT DEFENSIVE. Both row types are accordion
// click targets — the dark-pool header calls onToggle(row.symbol) and the UO row
// calls setUoDetail(...). Without it, charting a row also expands or collapses it,
// and the user leaves the tab having silently toggled something behind them.
const CHART_SYMBOL_KEY = "fterm_chart_symbol";
const CHART_TAB = "CHARTS";

function ChartJumpButton({ symbol, onNavigate }) {
  // ⚠ Rendered only when BOTH a symbol and a navigator exist. Without onNavigate
  // the button would seed a ticker and go nowhere, which reads as broken; better
  // to not offer it. That also keeps these tapes renderable outside App.
  const sym = String(symbol || "").trim().toUpperCase();
  if (!sym || typeof onNavigate !== "function") return null;
  return (
    <button
      title={`Chart ${sym} with the DP and UO concentration layers`}
      onClick={(e) => {
        e.stopPropagation();
        try { localStorage.setItem(CHART_SYMBOL_KEY, sym); } catch { /* private mode */ }
        onNavigate(CHART_TAB);
      }}
      style={{
        // Matched to the badges already on these rows (8px / 700 / 0.08em /
        // 1px border / radius 2) so it reads as row furniture, not a call to
        // action competing with the data.
        fontSize: 8, fontWeight: 700, letterSpacing: "0.08em",
        color: C.blue, background: "transparent",
        border: `1px solid ${C.blue}55`, borderRadius: 2,
        padding: "1px 5px", cursor: "pointer", whiteSpace: "nowrap",
        flexShrink: 0, lineHeight: 1.4,
      }}
    >
      CHART
    </button>
  );
}

// One tape row: collapsed summary, and the symbol's full prints when expanded.
// ⚠ Memoised on the fields that actually render, so a 30s tape refresh re-renders
// only the rows whose numbers moved — an expanded row with 45 print rows below it
// is not rebuilt because some other ticker printed.
const DpTapeRow = memo(function DpTapeRow({ row, open, detail, onToggle, onNavigate }) {
  const bias = row.net_bias || "NEUTRAL";
  const col = dpBiasColor(bias);
  const prints = (detail && detail.prints) || [];
  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <div
        onClick={() => onToggle(row.symbol)}
        title={`${row.symbol} — click to ${open ? "collapse" : "expand"} today's prints`}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, padding: "7px 10px", cursor: "pointer",
          background: open ? "rgba(255,255,255,0.04)" : "transparent",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = open ? "rgba(255,255,255,0.04)" : "transparent"; }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 9, color: C.textDim, width: 8 }}>{open ? "▾" : "▸"}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: "0.03em" }}>{row.symbol}</span>
          <span style={{
            fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", color: col,
            border: `1px solid ${col}`, borderRadius: 2, padding: "1px 5px", whiteSpace: "nowrap",
          }}>
            {bias}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 10, whiteSpace: "nowrap" }}>
          <span style={{ color: C.text, fontWeight: 700 }}>{dpNotional(row.total_notional)}</span>
          <span style={{ color: C.textDim }}>{row.total_prints} print{row.total_prints === 1 ? "" : "s"}</span>
          {/* ⚠ Before the clock, inside the existing right-hand flex group. That
              group has a fixed gap and no auto margins, so it simply grows
              leftward — the clock stays the rightmost element and nothing that
              was already on the row moves. */}
          <ChartJumpButton symbol={row.symbol} onNavigate={onNavigate} />
          <span style={{ color: C.textDim, minWidth: 38, textAlign: "right" }}>{dpEtClock(row.last_print_time)}</span>
        </div>
      </div>

      {open && (
        <div style={{ background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${C.border}`, padding: "6px 10px 8px" }}>
          {/* buyer/seller/midpoint can all read 0 with prints > 0 — ABOVE_MID,
              BELOW_MID and UNKNOWN increment no directional counter. Real state. */}
          <div style={{ fontSize: 9, color: C.textDim, marginBottom: 6 }}>
            {row.buyer_count} buyer · {row.seller_count} seller · {row.midpoint_count} midpoint
            {row.last_classification ? ` · last ${row.last_classification}` : ""}
          </div>

          {detail && detail.loading ? (
            <div style={{ fontSize: 10, color: C.textDim, padding: "8px 0" }}>Loading prints…</div>
          ) : detail && detail.error ? (
            <div style={{ fontSize: 10, color: C.red, padding: "8px 0" }}>Could not load prints for {row.symbol}.</div>
          ) : !prints.length ? (
            <div style={{ fontSize: 10, color: C.textDim, padding: "8px 0" }}>No individual prints retained for this symbol.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                <thead>
                  <tr style={{ color: C.textDim, fontSize: 8, letterSpacing: "0.08em" }}>
                    <th style={{ textAlign: "left",  padding: "2px 6px 4px 0" }}>TIME</th>
                    <th style={{ textAlign: "right", padding: "2px 6px 4px 0" }}>SIZE</th>
                    <th style={{ textAlign: "right", padding: "2px 6px 4px 0" }}>PRICE</th>
                    <th style={{ textAlign: "right", padding: "2px 6px 4px 0" }}>NOTIONAL</th>
                    <th style={{ textAlign: "left",  padding: "2px 6px 4px 0" }}>CLASSIFICATION</th>
                    <th style={{ textAlign: "left",  padding: "2px 0 4px 0" }}>STRENGTH</th>
                  </tr>
                </thead>
                <tbody>
                  {prints.map((p, i) => (
                    <tr key={`${p.timestamp || i}-${i}`} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: "3px 6px 3px 0", color: C.textDim }}>{dpEtClock(p.timestamp, true)}</td>
                      <td style={{ padding: "3px 6px 3px 0", textAlign: "right", color: C.text }}>
                        {Number(p.size || 0).toLocaleString()}
                      </td>
                      <td style={{ padding: "3px 6px 3px 0", textAlign: "right", color: C.text }}>
                        {Number(p.price || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: "3px 6px 3px 0", textAlign: "right", color: C.text, fontWeight: 700 }}>
                        {dpNotional(p.notional)}
                      </td>
                      <td style={{ padding: "3px 6px 3px 0", color: dpClassColor(p.classification) }}>
                        {p.classification || "—"}
                        {p.is_clean_cross === true && (
                          <span style={{ color: C.textDim, marginLeft: 5, fontSize: 8 }}>CLEAN</span>
                        )}
                      </td>
                      <td style={{ padding: "3px 0", color: dpClassColor(p.classification), letterSpacing: "0.1em" }}>
                        {dpDots(p.signal_strength)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 8, color: C.textDim, marginTop: 5 }}>
                {prints.length} print{prints.length === 1 ? "" : "s"} · newest first
                {prints.length >= 1000 ? " · capped at 1000" : ""}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export function DarkPoolTab({ keys, onData, focusSymbol, globalSignals, dpResults, onDpResultsUsed,
                             onNavigate }) {
  const [tape,      setTape]      = useState(null);    // /api/stream/darkpool/tape
  const [tapeErr,   setTapeErr]   = useState(false);
  const [agg,       setAgg]       = useState(null);    // /api/stream/darkpool/market
  const [filter,    setFilter]    = useState("");
  const [expanded,  setExpanded]  = useState(() => new Set());
  const [details,   setDetails]   = useState({});      // {SYM: {loading, prints, error}}
  const [updatedAt, setUpdatedAt] = useState(null);
  const [tick,      setTick]      = useState(0);
  // ⚠ Mirrors PhysicalIntelTab:158 — same 640px breakpoint, same listener shape.
  // Needed because the tape's height is an inline style and a media query cannot
  // be expressed there.
  const [narrow,    setNarrow]    = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
  );

  // Track the breakpoint so a rotation or resize re-heights the tape without a
  // remount. Cleaned up on unmount; the optional-call form matches the precedent
  // and tolerates older Safari, where addEventListener on a MediaQueryList is absent.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const h = (e) => setNarrow(e.matches);
    mq.addEventListener?.("change", h);
    return () => mq.removeEventListener?.("change", h);
  }, []);

  // Lazy-load bookkeeping read outside of render, so toggling a row never fires a
  // second fetch and no side effect runs inside a state updater.
  const detailsRef = useRef(details);
  useEffect(() => { detailsRef.current = details; }, [details]);

  // 30s clock: keeps the session label and the poll cadence honest across the
  // 09:30 / 16:00 boundaries without waiting for a fetch to land.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const et = useMemo(() => dpEtNow(), [tick]);
  const isOpen = et.weekday !== "Sat" && et.weekday !== "Sun"
    && et.minutes >= DP_OPEN_MIN && et.minutes < DP_CLOSE_MIN;

  const loadTape = useCallback(async () => {
    try {
      const res = await backendFetch("https://data.alpaca.markets", "/api/stream/darkpool/tape");
      if (!res.ok) throw new Error(String(res.status));
      const d = await res.json();
      setTape(d && Array.isArray(d.symbols) ? d : { symbols: [] });
      setUpdatedAt(Date.now());
      setTapeErr(false);
    } catch {
      setTapeErr(true);
    }
  }, []);

  const loadAgg = useCallback(async () => {
    try {
      const res = await backendFetch("https://data.alpaca.markets", "/api/stream/darkpool/market");
      if (!res.ok) throw new Error(String(res.status));
      setAgg(await res.json());
    } catch { /* header falls back to the tape's own totals */ }
  }, []);

  // ⚠ Keyed on isOpen so the cadence flips at the bell without a remount. The tape
  // itself is never gated — dp:summary carries a 24h TTL, so 03:00 still serves the
  // session that just ended.
  useEffect(() => {
    let alive = true;
    const run = () => { if (alive) { loadTape(); loadAgg(); } };
    run();
    const id = setInterval(run, isOpen ? DP_TAPE_POLL_OPEN_MS : DP_TAPE_POLL_CLOSED_MS);
    return () => { alive = false; clearInterval(id); };
  }, [isOpen, loadTape, loadAgg]);

  const loadDetail = useCallback(async (sym) => {
    setDetails((p) => ({ ...p, [sym]: { loading: true, prints: [], error: false } }));
    try {
      const res = await backendFetch("https://data.alpaca.markets", `/api/stream/darkpool/${sym}`);
      if (!res.ok) throw new Error(String(res.status));
      const d = await res.json();
      const prints = Array.isArray(d.recent_prints) ? [...d.recent_prints] : [];
      prints.sort((a, b) => String(b.timestamp || "").localeCompare(String(a.timestamp || "")));
      setDetails((p) => ({ ...p, [sym]: { loading: false, prints, error: false } }));
    } catch {
      setDetails((p) => ({ ...p, [sym]: { loading: false, prints: [], error: true } }));
    }
  }, []);

  const toggleRow = useCallback((sym) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym); else next.add(sym);
      return next;
    });
    if (!detailsRef.current[sym]) loadDetail(sym);
  }, [loadDetail]);

  // Squeeze / Convergence deep link (App passes dpFocus as {symbol, nonce}) — opens
  // that symbol's row in place rather than navigating to a detail view.
  useEffect(() => {
    const sym = focusSymbol && focusSymbol.symbol ? String(focusSymbol.symbol).trim().toUpperCase() : "";
    if (!sym) return;
    setExpanded((prev) => (prev.has(sym) ? prev : new Set(prev).add(sym)));
    if (!detailsRef.current[sym]) loadDetail(sym);
  }, [focusSymbol, loadDetail]);

  // ⚠ The Convergence Board still hands off scan results; there is no scan view to
  // receive them any more, so acknowledge the hand-off and let App clear its state.
  // Kept so App.jsx needs no change and its 60s cleanup timer is never orphaned.
  useEffect(() => {
    if (Array.isArray(dpResults) && dpResults.length) onDpResultsUsed?.();
  }, [dpResults, onDpResultsUsed]);

  const allRows = (tape && Array.isArray(tape.symbols)) ? tape.symbols : [];
  const rows = useMemo(() => {
    const q = filter.trim().toUpperCase();
    return q ? allRows.filter((r) => String(r.symbol || "").includes(q)) : allRows;
  }, [allRows, filter]);

  const hasAgg = agg && agg._source === "stream";
  const totalPrints   = hasAgg ? agg.total_prints   : (tape ? tape.total_prints   : 0);
  const totalNotional = hasAgg ? agg.total_notional : (tape ? tape.total_notional : 0);

  const stat = (label, value, color) => (
    <div style={{ minWidth: 78 }}>
      <div style={{ fontSize: 8, color: C.textDim, letterSpacing: "0.1em" }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: color || C.text }}>{value}</div>
    </div>
  );

  return (
    <div>
      {/* ── HEADER STRIP — always visible, never gated ── */}
      <div style={{ ...s.panel, marginBottom: 8 }}>
        <div style={s.panelH}>
          <span style={s.panelT}>DARK POOL TAPE</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: isOpen ? C.green : C.amber }}>
              {isOpen ? "● LIVE" : "● SESSION ENDED"}
            </span>
            <span style={{ fontSize: 9, color: C.textDim }}>
              {updatedAt ? `Updated ${new Date(updatedAt).toLocaleTimeString()}` : "Loading…"}
            </span>
          </div>
        </div>
        <div style={{ ...s.panelB, display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-start" }}>
          {stat("PRINTS TODAY", Number(totalPrints || 0).toLocaleString())}
          {stat("NOTIONAL", dpNotional(totalNotional))}
          {stat("BUYERS", hasAgg ? Number(agg.buyer_prints || 0).toLocaleString() : "—", C.green)}
          {stat("SELLERS", hasAgg ? Number(agg.seller_prints || 0).toLocaleString() : "—", C.red)}
          {stat("MIDPOINT", hasAgg ? Number(agg.midpoint_prints || 0).toLocaleString() : "—", C.teal)}
          {stat("SYMBOLS", tape ? Number(tape.total_symbols || 0).toLocaleString() : "—")}
        </div>
      </div>

      {/* ── FILTER — narrows the tape in place; never navigates, never refetches ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by ticker..."
          style={{
            flex: "0 1 220px", background: C.panel, border: `1px solid ${C.border}`,
            color: C.text, fontFamily: font, fontSize: 11, padding: "5px 8px",
            borderRadius: 3, textTransform: "uppercase", outline: "none",
          }}
        />
        {filter && (
          <button onClick={() => setFilter("")} style={{ ...s.btn("amber"), padding: "4px 10px", minHeight: 30, fontSize: 9 }}>
            CLEAR
          </button>
        )}
        <span style={{ fontSize: 9, color: C.textDim }}>
          {filter ? `${rows.length} of ${allRows.length} symbols` : `${allRows.length} symbols`}
        </span>
      </div>

      {/* ── THE TAPE ── */}
      <div style={{ ...s.panel }}>
        {/* ⚠ maxHeight, NOT height — the container must still shrink to fit when
            the tape is short, or a quiet session renders a large empty box below
            the last row. On mobile 62vh left the list capped at ~62% of the screen
            while the tab header, aggregate strip and filter row consumed the space
            above it, so the tape read as cut off at the bottom; calc(100vh - 180px)
            is the same viewport minus that chrome. Desktop is untouched. */}
        <div style={{ maxHeight: narrow ? "calc(100vh - 180px)" : "62vh", overflowY: "auto" }}>
          {tapeErr && !allRows.length ? (
            <div style={{ padding: 20, fontSize: 11, color: C.red, textAlign: "center" }}>
              Could not load the tape. Retrying automatically.
            </div>
          ) : !tape ? (
            <div style={{ padding: 20, fontSize: 11, color: C.textDim, textAlign: "center" }}>Loading tape…</div>
          ) : !allRows.length ? (
            <div style={{ padding: 20, fontSize: 11, color: C.textDim, textAlign: "center", lineHeight: 1.7 }}>
              No dark pool activity recorded for today&apos;s session yet.<br />
              Pre-market institutional flow will appear here as it arrives.
            </div>
          ) : !rows.length ? (
            <div style={{ padding: 20, fontSize: 11, color: C.textDim, textAlign: "center" }}>
              No ticker matching &ldquo;{filter.trim().toUpperCase()}&rdquo; on today&apos;s tape.
            </div>
          ) : (
            rows.map((r) => (
              <DpTapeRow
                key={r.symbol}
                row={r}
                open={expanded.has(r.symbol)}
                detail={details[r.symbol]}
                onToggle={toggleRow}
                onNavigate={onNavigate}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function DeepSignalsTab({ keys, onData }) {
  const [active,    setActive]   = useState("CONTRACTS");
  const [contracts, setContracts]= useState([]);
  // ⚠ An OBJECT with three sections, not a list. null means "not fetched";
  // a failed fetch leaves it null so the panel shows its honest error state.
  const [bio,       setBio]      = useState(null);
  // ⚠ An OBJECT of three zones, not a list. null means "not fetched"; a failed
  // fetch leaves it null so the panel shows its honest state and never invents.
  const [patents,   setPatents]  = useState(null);
  // Zone 1/2 accordion. A Set of "kind:id" strings so a ticker and a company
  // that happen to share a name cannot collide.
  const [patOpen,   setPatOpen]  = useState(() => new globalThis.Set());
  // Zone 3 is a walled-garden lookup: its own state, its own fetch, no bus
  // push, no AnalystButton, nothing shared with zones 1 and 2.
  const [patQuery,  setPatQuery]  = useState("");
  const [patHits,   setPatHits]   = useState(null);
  const [patSearch, setPatSearch] = useState("idle");
  const [courts,    setCourts]   = useState([]);
  const [banks,     setBanks]    = useState([]);
  // ⚠ Inline styles cannot express a media query, so the breakpoint is a state
  // flag driven by matchMedia — same pattern as the trade panel's tradeNarrow.
  const [bankNarrow, setBankNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );
  // Default order is assets-desc as the API returns it; clicking STATUS regroups
  // by severity so the stressed institutions come to the top.
  const [bankSort,  setBankSort]  = useState("assets");
  const [statusMap, setStatusMap]= useState({});

  const setStatus = (k, v) => setStatusMap((p) => ({ ...p, [k]: v }));

  const fetchContracts = useCallback(async (forceRefresh = false) => {
    setStatus("contracts", "loading");
    try {
      const res = await backendFetch(
        "https://api.usaspending.gov/api/v2/search/spending_by_award/",
        "/api/contracts" + forceParam(forceRefresh)
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const mapped = liveRows(await res.json());
      if (mapped.length) {
        setContracts(mapped);
        if (onData) onData("contracts", mapped);
        setStatus("contracts", "live");
      } else throw new Error("no results");
    } catch {
      setContracts([]);
      setStatus("contracts", "offline");
    }
  }, [onData]);

  // ⚠⚠ NO REPRESENTATIVE FALLBACK. The direct-mode URL is dropped too: it
  // carried a literal "+AND+" that openFDA parses as a plus sign, not a space,
  // and 500s on. Biotech intelligence is backend-only for that reason.
  const fetchFda = useCallback(async (forceRefresh = false) => {
    setStatus("fda", "loading");
    try {
      const res = await backendFetch(
        "https://clinicaltrials.gov/api/v2/studies",
        "/api/fda" + forceParam(forceRefresh)
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      // Live but quiet is still live: one populated section is enough.
      if (!data || !(data.phase3_readouts || data.recent_approvals || data.drug_shortages)) {
        throw new Error("empty");
      }
      setBio(data);
      setStatus("fda", "live");
    } catch {
      setBio(null);
      setStatus("fda", "offline");
    }
  }, []);

  // ⚠⚠ USPTO Open Data Portal, via our backend — NOT PatentsView. That host
  // does not resolve from Railway at all, so the old direct-from-browser call
  // could never have returned data. There is no direct-URL first argument here
  // on purpose: the ODP key is server-side and must never reach the client.
  const fetchPatents = useCallback(async (forceRefresh = false) => {
    setStatus("patents", "loading");
    try {
      const res = await backendFetch(
        "/api/patents/intelligence" + forceParam(forceRefresh),
        "/api/patents/intelligence" + forceParam(forceRefresh)
      );
      const data = await res.json();
      setPatents(data && typeof data === "object" ? data : null);
      setStatus("patents", "live");
    } catch {
      // ⚠ NO representative fallback. The old REP_PATENTS array rendered five
      // invented filings as real data whenever the upstream failed — which was
      // always. An honest empty panel is the correct output.
      setPatents(null);
      setStatus("patents", "offline");
    }
  }, []);

  // Zone 3. Deliberately NOT wired to onData, the signal bus, or any AI path.
  const runPatentSearch = useCallback(async (raw) => {
    // Client-side mirror of the server's sanitizer. Both are [a-zA-Z0-9 .-].
    const clean = String(raw || "").replace(/[^a-zA-Z0-9 .\-]/g, "").trim().slice(0, 100);
    if (clean.length < 2) { setPatSearch("short"); setPatHits(null); return; }
    setPatSearch("loading");
    try {
      const path = "/api/patents/search?q=" + encodeURIComponent(clean);
      const res = await backendFetch(path, path);
      if (res.status === 429) { setPatSearch("limited"); setPatHits(null); return; }
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setPatHits(Array.isArray(data?.results) ? data.results : []);
      setPatSearch("done");
    } catch {
      setPatHits(null);
      setPatSearch("error");
    }
  }, []);

  const fetchCourts = useCallback(async (forceRefresh = false) => {
    setStatus("courts", "loading");
    try {
      const res = await backendFetch(
        "https://www.courtlistener.com/api/rest/v3/dockets/" +
        "?order_by=-date_filed&nature_of_suit=850&format=json&page_size=12",
        "/api/courts" + forceParam(forceRefresh)
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const list = liveRows(await res.json());
      if (list.length) {
        setCourts(list);
        setStatus("courts", "live");
      } else throw new Error();
    } catch {
      setCourts([]);
      setStatus("courts", "offline");
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const h = (e) => setBankNarrow(e.matches);
    mq.addEventListener?.("change", h);
    return () => mq.removeEventListener?.("change", h);
  }, []);

  const fetchBanks = useCallback(async (forceRefresh = false) => {
    setStatus("banks", "loading");
    try {
      const res = await backendFetch(
        "https://banks.data.fdic.gov/api/institutions" +
        "?filters=ACTIVE%3A1" +
        "&fields=NAME,ASSET,NETINC,LNLSNET,REPDTE,STNAME" +
        "&limit=20&sort_by=ASSET&sort_order=DESC&output=json",
        "/api/banks" + forceParam(forceRefresh)
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.data || []);
      if (!list.length) throw new Error("empty");
      setBanks(list);
      setStatus("banks", "live");
    } catch {
      // ⚠⚠ NO REPRESENTATIVE FALLBACK. Real FDIC figures or an honest empty
      // state — never invented capital ratios for named institutions.
      setBanks([]);
      setStatus("banks", "offline");
    }
  }, []);

  useEffect(() => {
    if (active === "CONTRACTS" && contracts.length === 0) fetchContracts();
    if (active === "FDA"       && !bio)                   fetchFda();
    if (active === "PATENTS"   && patents           === null) fetchPatents();
    if (active === "COURTS"    && courts.length     === 0) fetchCourts();
    if (active === "BANKS"     && banks.length      === 0) fetchBanks();
  }, [active]);

  // ⚠ The CPC label now arrives from the backend (CPC_SECTION_LABELS), so the
  // old local cpcColors / cpcLabels maps are gone — two copies of the same
  // taxonomy in two languages is exactly how they drift.
  const patToggle = (kind, id) => setPatOpen((prev) => {
    const next = new globalThis.Set(prev);
    const key = kind + ":" + id;
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Manual refresh — force a fresh upstream fetch for the active panel.
  const refreshing = statusMap[active.toLowerCase()] === "loading";
  const handleRefresh = () => {
    if (active === "CONTRACTS")    fetchContracts(true);
    else if (active === "FDA")     fetchFda(true);
    else if (active === "PATENTS") fetchPatents(true);
    else if (active === "COURTS")  fetchCourts(true);
    else if (active === "BANKS")   fetchBanks(true);
  };

  return (
    <div>
      {/* Panel selector */}
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
        {DEEP_PANELS.map((p) => (
          <button
            key={p.id}
            onClick={() => setActive(p.id)}
            style={{ ...s.btn(active === p.id ? "amber" : "default"), padding: "4px 10px" }}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh — fetch fresh data now"
          style={{ ...s.btn("teal"), padding: "4px 10px", marginLeft: "auto" }}
        >
          <RefreshCw size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
          {refreshing ? "REFRESHING..." : "REFRESH"}
        </button>
      </div>

      {/* ── CONTRACTS ── */}
      {active === "CONTRACTS" && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>FEDERAL CONTRACT AWARDS</span>
            <DataStatus status={statusMap.contracts || "offline"} source="" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10, lineHeight: 1.65 }}>
              <span style={{ color: C.amber, fontWeight: 700 }}>SIGNAL MECHANIC:</span>{" "}
              Every federal contract is published within days of award —
              before press releases, before analyst updates. For companies where federal
              revenue is &gt;10% of total, awards of this scale are material.
            </div>
            {repUnavailable(statusMap.contracts, contracts) ? <UnavailableState /> : contracts.map((c, i) => (
              <div key={i} style={{ ...s.card(), borderLeft: `3px solid ${C.amber}`, marginBottom: 7 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1, marginRight: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.amber }}>{c.company}</div>
                    <div style={{ fontSize: 11, color: C.textDim, marginTop: 1 }}>{c.agency}</div>
                    <div style={{ fontSize: 11, color: C.textMid, marginTop: 2, fontStyle: "italic" }}>
                      {c.desc?.slice(0, 90)}{(c.desc?.length || 0) > 90 ? "..." : ""}
                    </div>
                    <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                      {c.state} · {c.date}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.green }}>
                      {fmtB(c.amount)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && !repUnavailable(statusMap.contracts, contracts) && (
              <AnalystButton
                panel="contracts"
                label="SYNTHESIZE"
                context={
                  `Federal contract awards (USASpending.gov):\n` +
                  contracts.slice(0, 6).map((c) =>
                    `${c.company}: ${fmtB(c.amount)} from ${c.agency} — ${c.desc?.slice(0, 60)}`
                  ).join("\n") +
                  `\n\nAnalyze concentration by agency, which companies are seeing increased federal revenue, ` +
                  `what sectors are seeing elevated government spending, ` +
                  `and any awards that appear anomalously large or unusual.`
                }
              />
            )}
          </div>
        </div>
      )}

      {/* ── BIOTECH INTELLIGENCE ── */}
      {/* ⚠⚠ THIS PANEL REPLACED A MISLABELLED ONE. It was headed "FDA APPROVALS
          & PIPELINE" with copy about PDUFA dates and AdCom votes, but rendered
          drug RECALLS — and styled every one of them with the rejection colour,
          because the badge tested for "APPROVED"/"PENDING" and a recall's field
          says "Class II". Nothing on screen was an approval. There is no free
          PDUFA feed (openFDA carries no goal-date field at all), so the copy no
          longer promises one. */}
      {active === "FDA" && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>BIOTECH INTELLIGENCE</span>
            <DataStatus
              status={statusMap.fda || "loading"}
              source={`${bio?.data_as_of ? `${bio.data_as_of}` : ""}`}
            />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10, lineHeight: 1.65 }}>
              <span style={{ color: "#ec4899", fontWeight: 700 }}>SIGNAL MECHANIC:</span>{" "}
              A Phase 3 primary completion date is a scheduled binary event —
              the trial either hits its endpoint or it does not, and biotech
              equities routinely move 40-80% on the result. Approvals confirm a
              revenue path; shortages mark supply stress at the manufacturer.
            </div>

            {!bio ? (
              <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.7, padding: "8px 0" }}>
                Biotech intelligence is temporarily unavailable. Trial and approval
                data refresh daily — check back shortly.
              </div>
            ) : (
              <>
                {/* ── SECTION 1 — Phase 3 readouts ── */}
                <BioSectionHead
                  title="Phase 3 Readouts"
                  sub={`${bio.trial_count ?? (bio.phase3_readouts || []).length} industry trials completing in the next 90 days`}
                />
                {!(bio.phase3_readouts || []).length ? (
                  <BioEmpty text="No Phase 3 industry trials completing in the next 90 days." />
                ) : bio.phase3_readouts.map((t) => (
                  <div key={t.nct_id} style={{ ...s.card(), display: "flex",
                                               justifyContent: "space-between",
                                               alignItems: "flex-start", gap: 10,
                                               flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                        {(t.intervention || t.title || "—").slice(0, 60)}
                      </div>
                      <div style={{ fontSize: 11, color: C.green, marginTop: 1 }}>{t.sponsor}</div>
                      {t.conditions && (
                        <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                          {t.conditions}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <BioDaysBadge days={t.days_until_completion} />
                      <div style={{ fontSize: 10, color: C.textDim, margin: "2px 0 4px" }}>
                        {t.completion_date}
                      </div>
                      <BioStatusPill status={t.status} />
                    </div>
                  </div>
                ))}

                {/* ── SECTION 2 — recent approvals ── */}
                <BioSectionHead title="Recent FDA Approvals" sub="NDA/BLA approvals, last 90 days" />
                {!(bio.recent_approvals || []).length ? (
                  <BioEmpty text="No recent approvals found." />
                ) : bio.recent_approvals.map((a) => (
                  <div key={a.application_number} style={{ ...s.card(), display: "flex",
                                                           justifyContent: "space-between",
                                                           alignItems: "flex-start", gap: 10,
                                                           flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                          {a.drug_name}
                        </span>
                        <span style={{ ...s.tag(C.textMid), fontSize: 9 }}>{a.application_type}</span>
                      </div>
                      <div style={{ fontSize: 11, color: C.green, marginTop: 1 }}>{a.company}</div>
                      {a.indication && (
                        <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                          {a.indication}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 11, color: C.textMid }}>{a.approval_date}</div>
                      <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>
                        {a.application_number}
                      </div>
                    </div>
                  </div>
                ))}

                {/* ── SECTION 3 — drug shortages ── */}
                <BioSectionHead title="Drug Shortages" sub="Current drug shortage list" />
                {!(bio.drug_shortages || []).length ? (
                  <BioEmpty text="No current shortages." />
                ) : bio.drug_shortages.map((d, i) => (
                  <div key={`${d.drug_name}-${i}`} style={{
                    ...s.card(),
                    /* ⚠ Red left border: a shortage is supply-chain stress, not
                       a neutral notice. */
                    borderLeft: `3px solid ${C.red}`,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                                  alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                            {d.drug_name}
                          </span>
                          <span style={{
                            fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 2,
                            background: C.redBg, color: C.red,
                          }}>
                            {d.shortage_status}
                          </span>
                        </div>
                        {d.company && (
                          <div style={{ fontSize: 11, color: C.green, marginTop: 1 }}>{d.company}</div>
                        )}
                        {d.reason && (
                          <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                            {d.reason}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: C.textDim, flexShrink: 0 }}>
                        {d.updated_date}
                      </div>
                    </div>
                  </div>
                ))}

                {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
                  <AnalystButton
                    panel="fda"
                    label="SYNTHESIZE"
                    context={
                      `Biotech intelligence as of ${bio.data_as_of} (ClinicalTrials.gov v2 + openFDA):\n` +
                      `Phase 3 readouts completing within 90 days (${bio.trial_count} total): ` +
                      (bio.phase3_readouts || []).slice(0, 10).map((t) =>
                        `${t.intervention || t.title} — ${t.sponsor} — ${t.conditions} — ${t.days_until_completion}d (${t.completion_date}, ${t.status})`
                      ).join("; ") +
                      `\nRecent NDA/BLA approvals: ` +
                      (bio.recent_approvals || []).slice(0, 8).map((a) =>
                        `${a.drug_name} (${a.application_type}) — ${a.company} — ${a.approval_date}`
                      ).join("; ") +
                      `\nCurrent drug shortages: ` +
                      (bio.drug_shortages || []).slice(0, 8).map((d) =>
                        `${d.drug_name} — ${d.company} — ${d.reason}`
                      ).join("; ") +
                      `\n\nAnalyse which sponsors face imminent binary readouts and how ` +
                      `concentrated their pipelines are, what the approvals imply for ` +
                      `revenue timing, and whether any shortage points at a manufacturer ` +
                      `with a supply or quality problem. These are scheduled dates, not ` +
                      `predictions — treat the completion date as the event risk.`
                    }
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── PATENTS ── */}
      {active === "PATENTS" && (() => {
        const tp   = patents?.ticker_patents || {};
        const sl   = patents?.sector_leaders || {};
        const tks  = Object.keys(tp);
        const secs = Object.keys(sl);
        const noThesis = !!patents?.note || (tks.length === 0 && secs.length === 0);
        // Macro carries no CPC prefixes, so a Macro-only thesis can never have
        // sector leaders — say why rather than showing an empty list.
        const macroOnly = secs.length > 0 && secs.every((x) => x === "Macro");

        const GrantRow = ({ g }) => (
          <div style={{ padding: "5px 0 5px 10px", borderLeft: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, color: C.text, lineHeight: 1.45 }}>{g.title || "—"}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 3 }}>
              {g.cpc_label && g.cpc_label !== "Unknown" && (
                <span style={{ fontSize: 9, color: C.blue, border: `1px solid ${C.blue}`,
                               borderRadius: 3, padding: "1px 5px", letterSpacing: "0.06em" }}>
                  {g.cpc_label}
                </span>
              )}
              <span style={{ fontSize: 10, color: C.textDim }}>
                {g.grant_date || "—"} · {g.patent_number || "—"}
              </span>
            </div>
          </div>
        );

        return (
          <div style={s.panel}>
            <div style={s.panelH}>
              <span style={s.panelT}>PATENT INTELLIGENCE</span>
              <DataStatus status={statusMap.patents || "offline"} source="" />
            </div>
            <div style={s.panelB}>
              <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10, lineHeight: 1.65 }}>
                <span style={{ color: C.purple, fontWeight: 700 }}>SIGNAL MECHANIC:</span>{" "}
                Granted patents are a 12-24 month trailing record of where R&D money
                already went. A company appearing in a CPC class it has not filed in
                before is a strategic pivot that predates any announcement.
                Utility patents only — ornamental design grants are excluded.
              </div>

              {/* ── ZONE 1 — YOUR TICKERS ── */}
              {tks.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                    Your Thesis Tickers
                  </div>
                  {tks.map((tk) => {
                    const row = tp[tk] || {};
                    const open = patOpen.has("t:" + tk);
                    return (
                      <div key={tk} style={s.card()}>
                        <div onClick={() => patToggle("t", tk)}
                             style={{ display: "flex", justifyContent: "space-between",
                                      alignItems: "center", cursor: "pointer", minHeight: 44 }}>
                          <div style={{ flex: 1, marginRight: 10 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: C.green }}>{tk}</span>
                            <span style={{ fontSize: 11, color: C.textDim, marginLeft: 8 }}>
                              {row.company || "—"}
                            </span>
                          </div>
                          <span style={{ fontSize: 10, color: C.textDim, flexShrink: 0 }}>
                            {row.grant_count || 0} grants · last 30 days {open ? "▴" : "▾"}
                          </span>
                        </div>
                        {open && (row.grants || []).length > 0 && (
                          <div style={{ marginTop: 6 }}>
                            {(row.grants || []).map((g) => <GrantRow key={g.patent_number} g={g} />)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {noThesis && (
                <div style={{ fontSize: 12, color: C.textDim, padding: "10px 0" }}>
                  Set up a Thesis Desk to see patent intelligence for your sectors.
                </div>
              )}

              {/* ── ZONE 2 — SECTOR LEADERS ── */}
              {macroOnly ? (
                <div style={{ fontSize: 12, color: C.textDim, padding: "10px 0" }}>
                  Patent intelligence is not available for macro theses — patents are
                  company and technology specific.
                </div>
              ) : secs.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                    Sector Patent Leaders
                  </div>
                  <div style={{ fontSize: 10, color: C.textDim, marginBottom: 6 }}>
                    Most active filers in your thesis sectors · Last 30 days · 2+ grants required
                  </div>
                  {secs.map((sec) => (
                    <div key={sec} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: C.purple, letterSpacing: "0.08em",
                                    borderBottom: `1px solid ${C.border}`, paddingBottom: 3,
                                    marginBottom: 4 }}>
                        {sec.toUpperCase()}
                      </div>
                      {(sl[sec] || []).length === 0 ? (
                        <div style={{ fontSize: 11, color: C.textDim, padding: "4px 0" }}>
                          No significant patent activity detected in {sec} this month.
                        </div>
                      ) : (sl[sec] || []).map((co) => {
                        const key = sec + "|" + co.company;
                        const open = patOpen.has("c:" + key);
                        return (
                          <div key={key} style={s.card()}>
                            <div onClick={() => patToggle("c", key)}
                                 style={{ display: "flex", justifyContent: "space-between",
                                          alignItems: "center", cursor: "pointer",
                                          gap: 8, minHeight: 44 }}>
                              <div style={{ flex: 1, display: "flex", alignItems: "center",
                                            gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 12, color: C.text }}>{co.company}</span>
                                {co.is_public ? (
                                  <span style={{ fontSize: 9, color: C.green,
                                                 border: `1px solid ${C.green}`, borderRadius: 3,
                                                 padding: "1px 5px" }}>
                                    PUBLIC{co.ticker ? ` · ${co.ticker}` : ""}
                                  </span>
                                ) : (
                                  /* ⚠⚠ noopener,noreferrer is MANDATORY — it stops the opened
                                     tab reaching back through window.opener. One way out. */
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(
                                        "https://www.google.com/search?q=" +
                                          encodeURIComponent(co.company + " company"),
                                        "_blank",
                                        "noopener,noreferrer"
                                      );
                                    }}
                                    title={`Search the web for ${co.company}`}
                                    style={{ fontSize: 9, color: C.amber, background: "transparent",
                                             border: `1px solid ${C.amber}`, borderRadius: 3,
                                             padding: "1px 5px", cursor: "pointer",
                                             fontFamily: "inherit" }}>
                                    PRIVATE ↗
                                  </button>
                                )}
                              </div>
                              <span style={{ fontSize: 10, color: C.textDim, flexShrink: 0 }}>
                                {co.grant_count} grants {open ? "▴" : "▾"}
                              </span>
                            </div>
                            {open && (co.top_grants || []).length > 0 && (
                              <div style={{ marginTop: 6 }}>
                                {(co.top_grants || []).map((g) => (
                                  <GrantRow key={g.patent_number} g={g} />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {/* ── ZONE 3 — SEARCH (walled garden) ──
                  Visually and functionally separated: its own divider, no
                  AnalystButton, no bus push, no synthesis. A standalone lookup. */}
              <div style={{ borderTop: `2px solid ${C.border}`, marginTop: 14, paddingTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Search Patents</div>
                <div style={{ fontSize: 10, color: C.textDim, marginBottom: 6 }}>
                  Search any company by name · patent grants · last 90 days
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    value={patQuery}
                    maxLength={100}
                    placeholder="Company name →"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { runPatentSearch(patQuery); return; }
                      // Block disallowed characters at the source; let control
                      // keys (Backspace, arrows, Tab, paste chords) through.
                      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey &&
                          !/[a-zA-Z0-9 .\-]/.test(e.key)) e.preventDefault();
                    }}
                    /* Mirrors the server sanitizer exactly — [a-zA-Z0-9 .-]. Catches
                       anything a paste slips past onKeyDown. Belt and braces. */
                    onChange={(e) => setPatQuery(e.target.value.replace(/[^a-zA-Z0-9 .\-]/g, ""))}
                    style={{ flex: "1 1 180px", minWidth: 140, background: C.panel,
                             border: `1px solid ${C.border}`, color: C.text,
                             fontFamily: font, fontSize: 11, padding: "6px 8px",
                             borderRadius: 3, textTransform: "uppercase" }}
                  />
                  <button onClick={() => runPatentSearch(patQuery)}
                          disabled={patSearch === "loading"}
                          style={{ ...s.btn("teal"), minHeight: 32, fontSize: 10 }}>
                    {patSearch === "loading" ? "SEARCHING…" : "SEARCH"}
                  </button>
                </div>

                {patSearch === "limited" && (
                  <div style={{ fontSize: 11, color: C.amber, textAlign: "center", padding: "10px 0" }}>
                    Search limit reached — 10 searches per hour maximum.
                  </div>
                )}
                {patSearch === "short" && (
                  <div style={{ fontSize: 11, color: C.textDim, padding: "8px 0" }}>
                    Enter at least two letters or numbers.
                  </div>
                )}
                {patSearch === "error" && (
                  <div style={{ fontSize: 11, color: C.textDim, padding: "8px 0" }}>
                    Patent search is unavailable right now.
                  </div>
                )}
                {patSearch === "done" && (patHits || []).length === 0 && (
                  <div style={{ fontSize: 11, color: C.textDim, padding: "8px 0" }}>
                    No utility patents found for “{patQuery}” in the last 90 days.
                  </div>
                )}
                {patSearch === "done" && (patHits || []).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {(patHits || []).map((g) => (
                      <div key={g.patent_number} style={s.card()}>
                        <div style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>
                          {g.company || "—"}
                        </div>
                        <GrantRow g={g} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── COURTS ── */}
      {active === "COURTS" && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>FEDERAL COURT WATCH</span>
            <DataStatus status={statusMap.courts || "offline"} source="" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10, lineHeight: 1.65 }}>
              <span style={{ color: C.red, fontWeight: 700 }}>SIGNAL MECHANIC:</span>{" "}
              Federal court filings are public within hours of filing — before any news coverage.
              Bankruptcy petitions, SEC enforcement actions, and class action complaints
              appear in court records first. Short sellers and distressed debt investors monitor daily.
            </div>
            {repUnavailable(statusMap.courts, courts) ? <UnavailableState /> : courts.map((c, i) => {
              const isBk  = c.nature?.toLowerCase().includes("bankruptcy") || c.name?.toLowerCase().includes("re:");
              const isSec = c.name?.toLowerCase().includes("sec");
              const isAt  = c.nature?.toLowerCase().includes("antitrust");
              const col   = isBk ? C.red : isSec ? C.amber : isAt ? C.orange : C.textMid;
              const type  = isBk ? "BANKRUPTCY" : isSec ? "SEC ACTION" : isAt ? "ANTITRUST" : "CIVIL";
              return (
                <div key={i} style={{ ...s.card(), borderLeft: `3px solid ${col}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 4 }}>
                        <span style={s.tag(col)}>{type}</span>
                        <span style={{ fontSize: 10, color: C.textDim }}>{c.court}</span>
                        <span style={{ fontSize: 10, color: C.textDim }}>{c.filed}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                    </div>
                    {c.url && (
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 13, color: C.blue, textDecoration: "none", flexShrink: 0, marginLeft: 8 }}
                      >
                        ↗
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
            {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && !repUnavailable(statusMap.courts, courts) && (
              <AnalystButton
                panel="signal_board"
                label="SYNTHESIZE"
                context={
                  `Federal court filings (CourtListener / PACER):\n` +
                  courts.slice(0, 6).map((c) =>
                    `${c.name} — ${c.court} — filed ${c.filed}` +
                    (c.nature ? ` — ${c.nature}` : "")
                  ).join("\n") +
                  `\n\nAnalyze: which cases are securities-related (SEC enforcement, fraud, ` +
                  `antitrust, bankruptcy), the companies named as defendants or plaintiffs, ` +
                  `the case types and what they imply, and which filings are recent enough ` +
                  `to precede news coverage.`
                }
              />
            )}
          </div>
        </div>
      )}

      {/* ── BANKS ── */}
      {active === "BANKS" && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>CALL REPORT DATA</span>
            <DataStatus status={statusMap.banks || "offline"} source="" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 10, lineHeight: 1.65 }}>
              <span style={{ color: C.teal, fontWeight: 700 }}>SIGNAL MECHANIC:</span>{" "}
              Every federally insured bank files quarterly Call Reports — fully public.
              Rising NPL ratios signal credit stress. Net interest margin compression signals
              rate sensitivity. Loan-to-deposit above 90% signals a stretched funding base.
            </div>
            {!banks.length ? (
              <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.7, padding: "8px 0" }}>
                Bank health data is temporarily unavailable. Data updates
                quarterly — check back shortly.
              </div>
            ) : (<>
            {/* ⚠ Quarterly data, said plainly — nobody should read a Call Report
                figure as a live one. */}
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8 }}>
              Call Report Data — {banks[0]?.report_date || "—"}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {[
                      { k: "name",  label: "BANK NAME",       always: true  },
                      { k: "state", label: "STATE",           always: false },
                      { k: "assets", label: "TOTAL ASSETS",   always: true  },
                      { k: "loans", label: "TOTAL LOANS",     always: false },
                      { k: "ni",    label: "NET INCOME",      always: false },
                      { k: "npl",   label: "NPL RATIO",       always: true  },
                      { k: "t1",    label: "TIER 1",          always: true  },
                      { k: "nim",   label: "NIM",             always: false },
                      { k: "ltd",   label: "LOAN-TO-DEPOSIT", always: false },
                      { k: "status", label: "STATUS",         always: true  },
                    ].filter((h) => h.always || !bankNarrow).map((h) => (
                      <th
                        key={h.k}
                        onClick={h.k === "status"
                          ? () => setBankSort((v) => (v === "status" ? "assets" : "status"))
                          : undefined}
                        title={h.k === "status" ? "Sort by severity" : undefined}
                        style={{
                          padding: "3px 7px", textAlign: "left", fontSize: 11,
                          color: h.k === "status" && bankSort === "status" ? C.teal : C.textDim,
                          fontWeight: 700, whiteSpace: "nowrap",
                          cursor: h.k === "status" ? "pointer" : "default",
                          userSelect: "none",
                        }}
                      >
                        {h.label}{h.k === "status" ? (bankSort === "status" ? " ▼" : " ⇅") : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(bankSort === "status"
                    ? [...banks].sort((x, y) =>
                        BANK_STATUS_RANK[bankStatus(x)] - BANK_STATUS_RANK[bankStatus(y)] ||
                        (y.assets || 0) - (x.assets || 0))
                    : banks
                  ).map((b, i) => {
                    const st = bankStatus(b);
                    return (
                      <tr key={`${b.name}-${i}`} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: "6px 7px" }}>
                          <div style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>{b.name}</div>
                        </td>
                        {!bankNarrow && (
                          <td style={{ padding: "6px 7px", fontSize: 11, color: C.textDim }}>{b.state}</td>
                        )}
                        <td style={{ padding: "6px 7px" }}>{fmtB(b.assets)}</td>
                        {!bankNarrow && (
                          <td style={{ padding: "6px 7px", color: C.textMid }}>{fmtB(b.loans)}</td>
                        )}
                        {!bankNarrow && (
                          <td style={{ padding: "6px 7px", fontWeight: 700,
                                       color: b.net_income == null ? C.textDim
                                            : b.net_income < 0 ? C.red : C.green }}>
                            {fmtB(b.net_income)}
                          </td>
                        )}
                        <td style={{ padding: "6px 7px", color: bankNplColor(b.npl_ratio) }}>
                          {bankPct(b.npl_ratio)}
                        </td>
                        <td style={{ padding: "6px 7px", color: bankTier1Color(b.tier1) }}>
                          {bankPct(b.tier1)}
                        </td>
                        {!bankNarrow && (
                          /* ⚠ No colour threshold on NIM by design — a "good" margin
                             is business-model dependent, so a scale would assert
                             something the number does not support. */
                          <td style={{ padding: "6px 7px", color: C.textMid }}>{bankPct(b.nim)}</td>
                        )}
                        {!bankNarrow && (
                          <td style={{ padding: "6px 7px", color: bankLtdColor(b.loan_to_deposit) }}>
                            {bankPct(b.loan_to_deposit)}
                          </td>
                        )}
                        <td style={{ padding: "6px 7px" }}>
                          <span style={s.tag(BANK_STATUS_COLOR[st])}>{st}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 6 }}>
              NPL = non-current loans / total loans · Tier 1 = risk-based capital ratio ·
              NIM = net interest margin · Quarterly Call Reports
            </div>
            </>)}
            {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && banks.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <AnalystButton
                  panel="banks"
                  label="SYNTHESIZE"
                  context={
                    `FDIC Call Report data (${banks[0]?.report_date || "latest quarter"}):\n` +
                    banks.slice(0, 8).map((b) =>
                      `${b.name} (${b.state}): assets ${fmtB(b.assets)}, ` +
                      `net income ${fmtB(b.net_income)}, loans ${fmtB(b.loans)}` +
                      (b.npl_ratio != null ? `, NPL ${b.npl_ratio}%` : "") +
                      (b.tier1 != null ? `, Tier1 ${b.tier1}%` : "") +
                      (b.nim != null ? `, NIM ${b.nim}%` : "") +
                      (b.loan_to_deposit != null ? `, loan-to-deposit ${b.loan_to_deposit}%` : "")
                    ).join("\n") +
                    `\n\nAnalyze health indicators, identify institutions showing stress patterns, ` +
                    `structural observations about credit quality or capital adequacy ` +
                    `relative to the current rate environment, ` +
                    `and any duration mismatch or liquidity red flags visible in the data.`
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
